import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { Switch } from "@components/ui/selection/Switch";
import { setBackgroundTask } from "@services/backgroundTasks";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DropDown from "@components/ui/dropdown/DropDown";
import OrdreStatusDropdown from "@components/ui/dropdown/OrdreStatusDropdown";
import AssignDropdown from "@components/ui/dropdown/AssignDropdown";
import InlineTagEditor from "@components/ui/tags/InlineTagEditor";
import DatePicker from "@components/ui/selection/DatePicker";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import CheckBox from "@components/ui/selection/CheckBox";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { useState, useRef, useEffect, useMemo } from "react";
import { useAlphaTransferStore } from "@services/serviceboxAlphaTransfer";
import Avvvatars from "avvvatars-react";
import ChatPanel from "@components/chat/ChatPanel";
import { ordresApi } from "@services/api/ordres";
import { appointmentsApi, type ReceptionConflictResult, type ReceptionConflictEvent } from "@services/api/appointments";
import { DocumentPreviewModal, galleryNav, type PreviewGalleryItem } from "@components/ui/DocumentPreviewModal";
import { dossiersApi } from "@services/api/dossiers";
import { resolveDocumentUrl, useDocumentUrl, type DocOwner, type ThumbDoc } from "@components/ui/DocThumbnail";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import { deleteOrdreInBackground } from "@services/ordreDelete";
import { useOrdreStore } from "@store/ordreStore";
import { serviceboxSyncApi } from "@services/api/serviceboxSync";
import { usePlanningStore } from "@store/planningStore";
import { navigateTo } from "context/navigation.service";
import { useAtelierStore, isMirroredJob } from "@store/atelierStore";
import InterventionEditModal from "@components/modals/InterventionEditModal";
import AdjustAtelierJobsModal from "@components/modals/AdjustAtelierJobsModal";
import { planJobShrink, type ShrinkPlan } from "@utils/atelierShrink";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { statusChangeUpdates } from "@store/arretJustificationStore";
import VehicleImage from "@components/ui/VehicleImage";
import RentalVehicleBadge, { MobilityRequestChip } from "@components/ui/RentalVehicleBadge";
import ClientMuteBadge from "@components/chat/ClientMuteBadge";
import BookingOriginBadge from "@components/BookingOriginBadge";
import InternetRdvBanner from "@components/InternetRdvBanner";
import AlphaUnknownBanner from "@components/AlphaUnknownBanner";
import SbSyncBanner from "@components/SbSyncBanner";
import VehicleEditModal from "@components/modals/VehicleEditModal";
import ContactFormModal from "@components/modals/ContactFormModal";
import type {
  Ordre,
  OrdreStatus,
  OrdreIntervention,
  OrdreDocument,
} from "types/ordre";
import type { DossierType } from "types/dossier";
import { DOSSIER_TYPE_OPTIONS } from "@constants/clientTypes";
import { withSinglePrimary, type Contact } from "types/contact";
import type { Dossier } from "types/dossier";
import type { ClientVehicle } from "types/client_vehicle";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { atelierApi } from "@services/api/atelier";
import type { WorkshopJob } from "types/garage/atelier";
import { recallCampaignsApi } from "@services/api/recallCampaigns";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import SecondaryOrdreModal from "@components/modals/SecondaryOrdreModal";
import OrdreActionsMenu from "@components/ui/OrdreActionsMenu";
import EditRentalReservationModal from "@components/modals/EditRentalReservationModal";
import { useOrdreMobilities } from "@components/ui/useOrdreMobilities";
import { FactureUploadModal, RentalVehicleModal, TrackingPreviewModal } from "@components/modals/OrdreActionModals";
import PaymentRequestModal from "@components/modals/PaymentRequestModal";
import EstimateExtractionModal from "@components/modals/EstimateExtractionModal";
import ServiceBoxTransferModal from "@components/modals/ServiceBoxTransferModal";
import ServiceBoxUpdateModal from "@components/modals/ServiceBoxUpdateModal";
import OrdreSyncHistoryModal from "@components/modals/OrdreSyncHistoryModal";
import ContratLocationModal from "@components/modals/ContratLocationModal";
import DevisReminderModal from "@components/modals/DevisReminderModal";
import { paymentsApi } from "@services/api/payments";
import { vehiclesApi } from "@services/api/vehicles";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import { serviceboxProxy, busyNotifier, type EstimationResult, type ForfaitChoice, type PrChoice } from "@services/serviceboxProxy";
import ServiceBoxPrChoiceModal from "@components/modals/ServiceBoxPrChoiceModal";
import { ORDRE_FLAG_CONFIG, ORDRE_FLAGS, isAPrevoir, getAssignedIds } from "types/ordre";
import type { OrdreFlag } from "types/ordre";
import type { Payment } from "types/payment";
import type { Vehicle } from "types/garage/vehicules";
import type { EstimateExtraction } from "types/estimateExtraction";
import ForceConfirmModal from "@components/modals/ForceConfirmModal";
import { bookingsApi, isForceable, type SlotValidationResult } from "@services/api/bookings";
import { isTimeOffGrid } from "@utils/timeGrid";
import { alphaClientNotUpdatedNotice } from "@utils/serviceboxTransfer";
import { useDocSendMenu } from "@components/ui/DocSendMenu";
import { useChatStageStore } from "@store/chatStageStore";

/** Minimum reception→restitution window (one 15-min slot) to forbid a zero-duration window. */
const MIN_WINDOW_SECONDS = 15 * 60;

/** Convert epoch seconds to local { date: "YYYY-MM-DD", time: "HH:MM" }. */
function secondsToDateTime(seconds: number): { date: string; time: string } {
  const d = new Date(seconds * 1000);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return { date, time };
}

// ─── Constants ─────────────────────────────────────────────

const TABS = [
  { value: "ordre", label: "Ordre" },
  { value: "interventions", label: "Interventions" },
  { value: "contacts", label: "Contacts" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
  { value: "communications", label: "Chat" },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
  { value: "arrete", label: "Arrêté" },
  { value: "annule", label: "Annulé" },
];


function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ─── Sub-modal for adding items ────────────────────────────

function AddSubModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name="add" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">{children}</div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Ajouter" onClick={onClose} />
      </div>
    </BaseModal>
  );
}

// ─── Tab content components ────────────────────────────────

function OrdreTab({
  form,
  update,
  peopleOnOrdre,
  linkedVehicle,
  dossierType,
  onDossierTypeChange,
  recalls,
  receptionConflict,
  onSwitchReception,
  receptionDisabledSlots,
  restitutionDisabledSlots,
}: {
  form: Ordre;
  update: <K extends keyof Ordre>(key: K, value: Ordre[K]) => void;
  peopleOnOrdre: { id: string; name: string; specialty: string; assigned: boolean; scheduled: boolean }[];
  linkedVehicle?: ClientVehicle | null;
  dossierType?: DossierType;
  onDossierTypeChange?: (type: DossierType | undefined) => void;
  recalls: { code: string; libelle: string; status: string }[];
  receptionConflict?: ReceptionConflictResult | null;
  onSwitchReception?: (ev: ReceptionConflictEvent, recId: string) => void;
  receptionDisabledSlots?: string[];
  restitutionDisabledSlots?: string[];
}) {
  // Auto-fill modèle from linked vehicle when vehicle changes
  const prevVehicleIdRef = useRef(linkedVehicle?.id);
  useEffect(() => {
    if (linkedVehicle && linkedVehicle.id !== prevVehicleIdRef.current) {
      const vehicleModele = [linkedVehicle.marque, linkedVehicle.modele].filter(Boolean).join(" ");
      if (vehicleModele) {
        update("modele", vehicleModele);
      }
    }
    prevVehicleIdRef.current = linkedVehicle?.id;
  }, [linkedVehicle?.id]);

  // Auto-set restitution when réception date (day) changes.
  // If only time changed (same day): only update restitution if reception time
  // goes past the current restitution time on that same day.
  const prevReceptionRef = useRef(form.reception_date?.seconds);
  useEffect(() => {
    const receptionSeconds = form.reception_date?.seconds;
    const prevSeconds = prevReceptionRef.current;
    if (
      receptionSeconds &&
      receptionSeconds !== prevSeconds
    ) {
      const receptionDate = new Date(receptionSeconds * 1000);
      const prevDate = prevSeconds ? new Date(prevSeconds * 1000) : null;
      const sameDayAsBefore =
        prevDate &&
        receptionDate.getFullYear() === prevDate.getFullYear() &&
        receptionDate.getMonth() === prevDate.getMonth() &&
        receptionDate.getDate() === prevDate.getDate();

      if (sameDayAsBefore) {
        // Only time changed — adjust restitution only if reception goes past it
        // and restitution is on the same day
        const restitutionSeconds = form.restitution?.seconds;
        if (restitutionSeconds) {
          const restitutionDate = new Date(restitutionSeconds * 1000);
          const sameDay =
            receptionDate.getFullYear() === restitutionDate.getFullYear() &&
            receptionDate.getMonth() === restitutionDate.getMonth() &&
            receptionDate.getDate() === restitutionDate.getDate();
          if (sameDay && receptionSeconds >= restitutionSeconds) {
            const twoHoursInSeconds = 2 * 60 * 60;
            update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
          }
        }
      } else {
        // Day changed — reset restitution to reception + 2h
        const twoHoursInSeconds = 2 * 60 * 60;
        update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
      }
    }
    prevReceptionRef.current = receptionSeconds;
  }, [form.reception_date?.seconds]);


  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Status + Assign + Tags — compact row like OrdreDetailScreen */}
      <div className="flex flex-row items-center gap-2 flex-wrap">
        <OrdreStatusDropdown
          currentStatus={form.status}
          onStatusChange={async (v) => {
            // Arrêter demande un motif (un tag suffit) ; sans motif, rien ne change.
            const extra = await statusChangeUpdates(form.id, v, form);
            if (!extra) return;
            if (extra.tag_ids) update("tag_ids", extra.tag_ids);
            if (extra.arret_reason) update("arret_reason", extra.arret_reason);
            update("status", v);
          }}
        />
        <AssignDropdown
          assignedIds={getAssignedIds(form)}
          onChange={(userIds) => update("assigned_to_ids", userIds)}
        />
        {form.status === "arrete" && form.arret_reason?.label && (
          <span
            title={form.arret_reason.note || undefined}
            className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
            style={{ background: "#FEE2E2", color: "#B91C1C" }}
          >
            <Icon1 name="warning" size={12} color="#B91C1C" />
            Arrêté : {form.arret_reason.label}
          </span>
        )}
        {peopleOnOrdre.map((p) => {
          const color = p.specialty === "carrosserie" ? "#92400E" : p.specialty === "mecanique" ? "#1E40AF" : "#374151";
          const bg = p.specialty === "carrosserie" ? "#FEF3C7" : p.specialty === "mecanique" ? "#DBEAFE" : "#F3F4F6";
          const icon = p.assigned ? "user" : "side-ordres";
          const title = p.assigned && p.scheduled
            ? "Assigné · planifié à l'atelier"
            : p.assigned ? "Assigné" : "Planifié à l'atelier";
          return (
            <span
              key={p.id}
              title={title}
              className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
              style={{ background: bg, color }}
            >
              <Icon1 name={icon} size={12} color={color} />
              {p.name}
            </span>
          );
        })}
        {form.amount != null && (
          <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
            {form.amount.toFixed(2)} € TTC
          </span>
        )}
      </div>
      <InlineTagEditor
        scope="ordre"
        alwaysShowAdd
        tagIds={form.tag_ids || []}
        onSave={(ids) => {
          update("tag_ids", ids);
          // Les tags se posent tout de suite, sans attendre « Enregistrer » —
          // comme dans la liste et la fiche. Ils partaient sinon à la poubelle :
          // l'enregistrement n'envoyait pas tag_ids.
          ordresApi.update(form.id, { tag_ids: ids })
            .then((updated) => useOrdreStore.getState().updateOrdre(updated))
            .catch(() => showNotification({ type: "error", message: "Tags non enregistrés" }));
        }}
      />

      {recalls.length > 0 && (
        <div className="flex flex-col gap-1 px-3 py-2 rounded-lg" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <div className="flex flex-row items-center gap-2">
            <Icon1 name="warning" size={14} color="#DC2626" />
            <span className="text-h3 font-semibold" style={{ color: "#DC2626" }}>
              Campagne de rappel ({recalls.length})
            </span>
          </div>
          {recalls.map((c) => (
            <div key={c.code || c.libelle} className="flex flex-row items-center gap-2">
              <span className="text-h4" style={{ color: "#991B1B" }}>{c.code} — {c.libelle}</span>
              <span
                className="text-h5 font-medium rounded-full px-2 py-0.5"
                style={{
                  background: c.status === "treated" ? "#D1FAE5" : c.status === "acknowledged" ? "#FEF3C7" : "#FEE2E2",
                  color: c.status === "treated" ? "#065F46" : c.status === "acknowledged" ? "#92400E" : "#991B1B",
                }}
              >
                {c.status === "treated" ? "Traité" : c.status === "acknowledged" ? "Pris en compte" : "Détecté"}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="N° Ordre" value={form.num} onChange={(v) => update("num", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Nom" value={form.name} onChange={(v) => update("name", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DropDown
            label="Type de dossier"
            value={dossierType || ""}
            options={DOSSIER_TYPE_OPTIONS}
            onSelect={(v) => onDossierTypeChange?.((v || undefined) as DossierType | undefined)}
          />
        </div>
        <div className="flex-1">
          <TextField
            label="Montant TTC (€)"
            value={form.amount != null ? `${form.amount.toFixed(2)} €` : ""}
            placeholder="Pas encore de devis récupéré de ServiceBox"
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Immatriculation" value={form.immatriculation || ""} onChange={(v) => update("immatriculation", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={form.modele || ""} onChange={(v) => update("modele", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DateTimePicker
            label="Date réception"
            seconds={form.reception_date?.seconds || null}
            disabledSlots={receptionDisabledSlots}
            onChange={(s) => {
              update("reception_date", { seconds: s });
              // Restitution must stay strictly after reception (min one slot)
              if (form.restitution?.seconds && form.restitution.seconds < s + MIN_WINDOW_SECONDS) {
                update("restitution", { seconds: s + MIN_WINDOW_SECONDS });
              }
            }}
          />
        </div>
        <div className="flex-1">
          <DateTimePicker
            label="Date restitution"
            seconds={form.restitution?.seconds || null}
            disabledSlots={restitutionDisabledSlots}
            onChange={(s, forced) => {
              const rec = form.reception_date?.seconds || 0;
              // Restitution can't be at or before reception — enforce a minimum window.
              // "Forcer" in the time picker means the staff picked that exact hour on
              // purpose: clamping it there would silently undo the choice.
              const minSeconds = rec ? rec + MIN_WINDOW_SECONDS : 0;
              update("restitution", { seconds: forced ? s : Math.max(s, minSeconds) });
            }}
          />
        </div>
      </div>
      {!form.restitution?.seconds && (
        <span className="text-h4 text-red-500">La date de restitution est obligatoire</span>
      )}
      {(isTimeOffGrid(form.reception_date?.seconds) || isTimeOffGrid(form.restitution?.seconds)) && (
        <span className="text-h4 text-orange-600">
          Horaire hors créneau de 7,5 min
          {isTimeOffGrid(form.reception_date?.seconds) ? ` (réception ${new Date((form.reception_date!.seconds) * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})` : ""}
          {isTimeOffGrid(form.restitution?.seconds) ? ` (restitution ${new Date((form.restitution!.seconds) * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})` : ""}
          {" "}— peut être refusé par ServiceBox.
        </span>
      )}
      {/* Status that never reached ServiceBox. ServiceBox validates the whole RDV form
          on save, so a push is refused for things unrelated to the status — until now
          that only ever produced an e-mail, and the ordre looked perfectly in sync. */}
      {form.sb_push_last && form.sb_push_last.success === false && (
        <div
          className="flex flex-col gap-1 p-3 rounded-lg"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
        >
          <span className="text-h4 font-medium" style={{ color: "#991B1B" }}>
            ⚠ Statut non synchronisé vers ServiceBox
            {form.sb_push_last.retrying ? " (nouvelle tentative en cours)" : ""}
          </span>
          <span className="text-h4" style={{ color: "#7F1D1D" }}>
            ServiceBox a refusé la sauvegarde : {form.sb_push_last.error || "motif inconnu"}
          </span>
          <span className="text-h5" style={{ color: "#B91C1C" }}>
            Le RDV affiche encore son état précédent côté ServiceBox. Corrigez le point
            ci-dessus sur le RDV, le prochain changement de statut repoussera.
          </span>
        </div>
      )}
      {(form.sb_push_last?.repairs?.length ?? 0) > 0 && form.sb_push_last?.success && (
        <span className="text-h5 text-orange-600">
          RDV corrigé pour que ServiceBox accepte la sauvegarde : {form.sb_push_last!.repairs!.join(" ; ")}
        </span>
      )}
      {/* ServiceBox slot-collision warning (reception/restitution on a taken column) */}
      {[receptionConflict?.reception, receptionConflict?.restitution]
        .filter((e): e is ReceptionConflictEvent => !!e && e.conflict)
        .map((ev) => (
          <div
            key={ev.type}
            className="flex flex-col gap-2 p-3 rounded-lg"
            style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
          >
            <span className="text-h4 font-medium" style={{ color: "#92400E" }}>
              ⚠ Le créneau de {ev.type === "reception" ? "réception" : "restitution"} ({ev.conflict_with?.time}) est déjà
              occupé en {ev.category_label}
              {ev.conflict_with?.client_name ? ` par ${ev.conflict_with.client_name}` : ""}
              {ev.conflict_with?.plate ? ` (${ev.conflict_with.plate})` : ""}. Le transfert ServiceBox sera refusé.
            </span>
            {ev.free_receptions.length > 0 ? (
              <div className="flex flex-row flex-wrap gap-2">
                {ev.free_receptions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => onSwitchReception?.(ev, r.id)}
                    className="px-3 py-1.5 rounded-lg text-h4 font-medium transition-opacity hover:opacity-80"
                    style={{ background: "#FFFFFF", border: "1px solid #FCD34D", color: "#92400E" }}
                  >
                    Basculer sur {r.label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="text-h5" style={{ color: "#92400E", opacity: 0.85 }}>
                Aucune autre réception libre sur ce créneau.
              </span>
            )}
          </div>
        ))}
      <AreaTextField
        label="Notes"
        value={form.notes || ""}
        onChange={(v) => update("notes", v)}
        rows={3}
      />
    </div>
  );
}

// ─── Intervention Picker Modal ─────────────────────────────

const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision", reparation: "Réparation / Panne", diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique", pneumatique: "Pneus", carrosserie: "Carrosserie / Vitrage",
  rappel_constructeur: "Rappel constructeur",
};
const INTERVENTION_TYPE_OPTIONS = Object.entries(INTERVENTION_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

/** Devis initial (ou RDV MyO) : seul groupe planifié automatiquement. Un devis
 *  complémentaire validé ne crée qu'une charge « à répartir », placée à la main. */
const isInitialGroup = (group?: string) => !group || group === "initial" || group === "servicebox";

function InterventionPickerModal({ onClose, onAdd, configuredInterventions, onCatalogUpdated, group = "initial" }: {
  onClose: () => void;
  onAdd: (inters: OrdreIntervention[], autoPlace: boolean) => void;
  configuredInterventions: Intervention[];
  onCatalogUpdated: (items: Intervention[]) => void;
  group?: string;
}) {
  const workers = useAtelierStore((s) => s.workers);
  const workerOptions = [
    { value: "", label: "Non assigné" },
    ...workers.map((w) => ({ value: w.id, label: w.name })),
  ];

  const [mode, setMode] = useState<"catalog" | "new">("catalog");
  const [selectedItems, setSelectedItems] = useState<Intervention[]>([]);
  const [search, setSearch] = useState("");
  const [label, setLabel] = useState("");
  const [detail, setDetail] = useState("");
  const [estimatedH, setEstimatedH] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  // Planif auto par défaut : on la choisit ici plutôt qu'à chaque enregistrement.
  const [autoPlace, setAutoPlace] = useState(true);
  const canAutoPlace = isInitialGroup(group);

  const resetForm = () => {
    setSelectedItems([]); setLabel(""); setDetail(""); setEstimatedH(""); setAssignedTo(""); setSearch("");
  };

  // Multi-select: toggle an item. When exactly one is selected, expose its
  // editable detail (libellé / heures / assigné). With several selected, each is
  // added with its catalogue defaults.
  const toggleCatalog = (item: Intervention) => {
    setSelectedItems((prev) => {
      const exists = prev.some((p) => p.id === item.id);
      const next = exists ? prev.filter((p) => p.id !== item.id) : [...prev, item];
      if (next.length === 1) {
        const only = next[0];
        const h = +(only.estimated_minutes / 60).toFixed(2);
        setLabel(only.name); setEstimatedH(String(h)); setAssignedTo("");
      } else {
        setLabel(""); setEstimatedH(""); setAssignedTo("");
      }
      return next;
    });
  };

  const canAdd = mode === "catalog" ? selectedItems.length > 0 : label.trim() !== "";

  const handleAdd = () => {
    if (!canAdd) return;
    if (mode === "new") {
      // Intervention specific to THIS ordre only — not saved to the catalog.
      onAdd([{
        id: `i-${Date.now()}`,
        label,
        detail: detail.trim() || undefined,
        estimated_hours: parseFloat(estimatedH) || 0,
        assigned_to: assignedTo,
        group,
      }], canAutoPlace && autoPlace);
    } else {
      const single = selectedItems.length === 1;
      const inters: OrdreIntervention[] = selectedItems.map((item, idx) => {
        const h = +(item.estimated_minutes / 60).toFixed(2);
        return {
          id: `i-${Date.now()}-${idx}`,
          label: single ? label : item.name,
          // La précision ne vaut que pour une ligne : sur un ajout groupé, chacune
          // se précise ensuite dans la liste.
          detail: single && detail.trim() ? detail.trim() : undefined,
          estimated_hours: single ? (parseFloat(estimatedH) || h) : h,
          assigned_to: single ? assignedTo : "",
          group,
          forfait_code: item.forfait_code || undefined,
        };
      });
      onAdd(inters, canAutoPlace && autoPlace);
    }
    onClose();
  };

  return (
    <BaseModal title="Ajouter une intervention" startIcon={<Icon1 name="plus" size={20} />} onClose={onClose} width="max-w-lg">
      <div className="p-4 flex flex-col gap-4">
        <ChunkyTabs
          activeTab={mode}
          tabs={[{ value: "catalog", label: "Depuis le catalogue" }, { value: "new", label: "Nouvelle" }]}
          setActiveTab={(t) => { setMode(t as "catalog" | "new"); resetForm(); }}
        />

        {mode === "catalog" && (
          <div className="flex flex-col gap-3">
            <TextField placeholder="Rechercher une intervention…" value={search} onChange={setSearch} />
            <div className="overflow-y-auto rounded-lg border border-neutral-200" style={{ maxHeight: 220 }}>
              {Object.entries(INTERVENTION_TYPE_LABELS).map(([cat, catLabel]) => {
                const q = search.trim().toLowerCase();
                const items = configuredInterventions
                  .filter((i) => i.type === cat && i.active && (!q || i.name.toLowerCase().includes(q)))
                  .sort((a, b) => a.name.localeCompare(b.name, "fr"));
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1 text-h5 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">{catLabel}</div>
                    {items.map((item) => {
                      const h = +(item.estimated_minutes / 60).toFixed(2);
                      const isSelected = selectedItems.some((s) => s.id === item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => toggleCatalog(item)}
                          className={`flex flex-row items-center gap-2 w-full px-3 py-2 text-left transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-neutral-50"}`}
                        >
                          <span
                            className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0"
                            style={{ borderColor: isSelected ? "#6366f1" : "#D4D4D4", background: isSelected ? "#6366f1" : "transparent" }}
                          >
                            {isSelected && (
                              <svg width={9} height={7} viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" /></svg>
                            )}
                          </span>
                          <span className={`text-h3 flex-1 ${isSelected ? "text-indigo-700 font-medium" : "text-neutral-600"}`}>{item.name}</span>
                          <span className="text-h4 text-neutral-400 ml-2 flex-shrink-0">{h}h</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {(() => {
                const q = search.trim().toLowerCase();
                const matches = configuredInterventions.filter((i) => i.active && (!q || i.name.toLowerCase().includes(q)));
                if (matches.length > 0) return null;
                return (
                  <div className="px-3 py-6 text-h3 text-neutral-400 text-center">
                    {q ? "Aucune intervention trouvée" : "Aucune intervention dans le catalogue"}
                  </div>
                );
              })()}
            </div>
            {selectedItems.length === 1 && (
              <div className="flex flex-col gap-3 pt-2 border-t border-neutral-100">
                <TextField label="Libellé" value={label} onChange={setLabel} />
                <TextField label="Précision (optionnel)" value={detail} onChange={setDetail} placeholder="Symptôme, contexte…" />
                <div className="flex flex-row gap-2">
                  <div className="flex-1">
                    <TextField label="Heures estimées" type="number" value={estimatedH} onChange={setEstimatedH} />
                  </div>
                  <div className="flex-1">
                    <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
                  </div>
                </div>
              </div>
            )}
            {selectedItems.length > 1 && (
              <div className="flex flex-col gap-1 pt-2 border-t border-neutral-100">
                <span className="text-h3 font-medium text-indigo-700">{selectedItems.length} interventions sélectionnées</span>
                <span className="text-h4 text-neutral-400">{selectedItems.map((s) => s.name).join(", ")}</span>
                <span className="text-h5 text-neutral-400">Chacune sera ajoutée avec sa durée estimée. Vous pourrez assigner les mécaniciens depuis l'atelier.</span>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="flex flex-col gap-3">
            <TextField label="Libellé" value={label} onChange={setLabel} />
            <TextField label="Précision (optionnel)" value={detail} onChange={setDetail} placeholder="Symptôme, contexte…" />
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Durée estimée (h)" type="number" value={estimatedH} onChange={setEstimatedH} />
              </div>
              <div className="flex-1">
                <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
              </div>
            </div>
            <span className="text-h4 text-neutral-400">Intervention spécifique à cet ordre (non ajoutée au catalogue).</span>
          </div>
        )}
        {canAutoPlace && assignedTo && (mode === "new" || selectedItems.length === 1) && (
          <label className="flex flex-row items-start gap-2 cursor-pointer select-none">
            <input type="checkbox" className="mt-1" checked={autoPlace} onChange={(e) => setAutoPlace(e.target.checked)} />
            <span className="flex flex-col">
              <span className="text-h3 text-neutral-700">Planifier automatiquement sur le planning atelier</span>
              <span className="text-h5 text-neutral-400">
                {autoPlace
                  ? "Placé au premier créneau libre du mécanicien à l'enregistrement."
                  : "Placement manuel : l'intervention n'est pas posée sur le planning."}
              </span>
            </span>
          </label>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={mode === "catalog" && selectedItems.length > 1 ? `Ajouter (${selectedItems.length})` : "Ajouter"} onClick={handleAdd} disabled={!canAdd} />
      </div>
    </BaseModal>
  );
}

// ContactFormModal is now imported from @components/modals/ContactFormModal

function AddVehicleModal({ onClose }: { onClose: () => void }) {
  const [plate, setPlate] = useState("");
  const [marque, setMarque] = useState("");
  const [modele, setModele] = useState("");
  const [vin, setVin] = useState("");
  return (
    <AddSubModal title="Ajouter un véhicule" onClose={onClose}>
      <TextField label="Immatriculation" value={plate} onChange={setPlate} />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Marque" value={marque} onChange={setMarque} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={modele} onChange={setModele} />
        </div>
      </div>
      <TextField label="VIN" value={vin} onChange={setVin} />
    </AddSubModal>
  );
}

// ─── Tab content components (Interventions, Atelier, Contacts, Vehicules, Documents) ──

const GROUP_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  devis_sent: "Devis envoye",
  devis_signed: "Devis signe",
  ready: "Pret",
};

const GROUP_STATUS_COLORS: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-500",
  devis_sent: "bg-blue-100 text-blue-700",
  devis_signed: "bg-amber-100 text-amber-700",
  ready: "bg-green-100 text-green-700",
};

interface AtelierChargeDisplay {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  is_placed: boolean;
  is_auto: boolean;
}

/**
 * Le planning garde-t-il plus d'heures que le travail n'en demande ?
 *
 * Seuls les blocs posés depuis Carvis comptent : un magnet miroité de ServiceBox
 * appartient au réconciliateur, qui le remettrait comme il était (sauf si Carvis fait
 * foi : il n'y a plus de réconciliateur, voir isMirroredJob).
 */
/** Champs que le serveur réécrit aussi (devis importé/validé, synchro SB). */
const SERVER_OWNED_FIELDS = ["interventions", "documents", "status"] as const;

function planAtelierShrink(ordreId: string, interventions: OrdreIntervention[]): ShrinkPlan {
  const { jobs, workers } = useAtelierStore.getState();
  const needed = interventions
    .filter((i) => !isAPrevoir(i))
    .reduce((sum, i) => sum + (i.estimated_hours || 0), 0);
  const placed = jobs
    .filter((j) => j.ordre_id === ordreId && !isMirroredJob(j))
    .map((j) => ({
      id: j.id,
      date: j.date,
      startHour: j.startHour,
      durationHours: j.durationHours,
      workerName: workers.find((w) => w.id === j.workerId)?.name,
    }));
  return planJobShrink(placed, needed);
}

function ModalGroupSection({
  title,
  group,
  interventions,
  groupStatus,
  onAdd,
  onRemove,
  onToggle,
  onEdit,
}: {
  title: string;
  group: string;
  interventions: OrdreIntervention[];
  groupStatus?: { status: string };
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
  onToggle: (idx: number) => void;
  onEdit: (idx: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const status = groupStatus?.status || "draft";
  const workers = useAtelierStore((s) => s.workers);
  const workerName = (id: string) => workers.find((w) => w.id === id)?.name || id;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">{title}</span>
          <span className="text-h4 text-neutral-400">({interventions.length})</span>
        </div>
        <span className={`text-h5 font-medium px-2 py-0.5 rounded-full ${GROUP_STATUS_COLORS[status] || ""}`}>
          {GROUP_STATUS_LABELS[status] || status}
        </span>
      </div>
      {!collapsed && (
        <div className="p-3 flex flex-col gap-2">
          {interventions.map((inter, i) => {
            const aPrevoir = isAPrevoir(inter);
            return (
            <div
              key={inter.id || i}
              className={`flex flex-row items-center justify-between rounded-lg border px-3 py-2 ${
                aPrevoir ? "bg-amber-50/50 border-amber-200" : "bg-white border-neutral-200"
              }`}
            >
              <div className={`flex flex-col flex-1 min-w-0 ${aPrevoir ? "opacity-70" : ""}`}>
                <div className="flex flex-row items-center gap-2 flex-wrap">
                  <span className={`text-h3 font-medium min-w-0 ${aPrevoir ? "line-through decoration-amber-400/60" : ""}`}>
                    {inter.label || "Nouvelle intervention"}
                  </span>
                  {aPrevoir && (
                    <span
                      className="text-h5 font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                      title="Enregistré sur le dossier — non facturé sur ce RDV, reproposé à la prochaine prise de RDV"
                    >
                      À prévoir
                    </span>
                  )}
                  {inter.forfait_code && (
                    <span
                      className="text-h4 font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200"
                      title="Code forfait ServiceBox (transféré comme Forfait Local)"
                    >
                      Forfait {inter.forfait_code}
                    </span>
                  )}
                </div>
                {inter.detail && (
                  <span className="text-h4 text-neutral-600">{inter.detail}</span>
                )}
                <span className="text-h4 text-neutral-400">
                  {inter.estimated_hours || 0}h
                  {inter.assigned_to ? ` — ${workerName(inter.assigned_to)}` : ""}
                </span>
              </div>
              <div className="flex flex-row items-center gap-1 flex-shrink-0">
                <button
                  className="p-1 rounded hover:bg-neutral-100 transition-colors"
                  onClick={() => onEdit(i)}
                  title="Modifier l'intervention"
                >
                  <Icon1 name="modify" size={14} color="#A1A1A1" />
                </button>
                <button
                  className={`text-h5 font-medium px-2 py-1 rounded border transition-colors ${
                    aPrevoir
                      ? "border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                      : "border-amber-200 text-amber-700 hover:bg-amber-50"
                  }`}
                  onClick={() => onToggle(i)}
                  title={aPrevoir ? "Réintégrer cette intervention dans le RDV" : "Basculer en travail à prévoir (enregistré sur le dossier)"}
                >
                  {aPrevoir ? "Inclure" : "À prévoir"}
                </button>
                <button onClick={() => onRemove(i)} className="p-1 rounded hover:bg-neutral-100 transition-colors">
                  <Icon1 name="close" size={14} color="#A1A1A1" />
                </button>
              </div>
            </div>
            );
          })}
          {interventions.length === 0 && (
            <span className="text-h3 text-neutral-400 py-3 text-center">Aucune intervention</span>
          )}
          <div className="mt-1">
            <SecondaryButton variant="small" startIcon={<Icon1 name="plus" size={14} />} label="Ajouter" onClick={() => onAdd(group)} />
          </div>
        </div>
      )}
    </div>
  );
}

const SPECIALTY_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  mecanique:     { bg: "#DBEAFE", color: "#1E40AF", label: "Méca" },
  carrosserie:   { bg: "#FEF3C7", color: "#92400E", label: "Carross." },
  peinture:      { bg: "#FEE2E2", color: "#DC2626", label: "Peinture" },
  diagnostique:  { bg: "#D1FAE5", color: "#059669", label: "Diag." },
  service_rapide:{ bg: "#EDE9FE", color: "#6F61FF", label: "Serv. rapide" },
  campagnes:     { bg: "#FFEDD5", color: "#EA580C", label: "Campagnes" },
  autre:         { bg: "#F3F4F6", color: "#374151", label: "Autre" },
};

function AtelierPlanningSection({ charges }: { charges: AtelierChargeDisplay[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const placed   = charges.filter((c) => c.is_placed);
  const unplaced = charges.filter((c) => !c.is_placed);
  const hasAuto  = placed.some((c) => c.is_auto);

  // à planifier (charge non placée) > à confirmer (placement auto) > Planifié (confirmé)
  const statusBadge = charges.length === 0 ? null : unplaced.length > 0
    ? <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{unplaced.length} a planifier</span>
    : hasAuto
    ? <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">A confirmer</span>
    : <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Planifie</span>;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">Planification atelier</span>
          <span className="text-h4 text-neutral-400">({charges.length})</span>
          {statusBadge}
        </div>
      </div>

      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {charges.length === 0 && (
            <div className="px-3 py-4 text-center text-h3 text-neutral-400">Aucune charge atelier</div>
          )}

          {placed.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {c.worker_name}
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h3 text-neutral-600">{c.date.split("-").reverse().join("/")}</span>
                <span className="text-h3 text-neutral-500">{formatHour(c.start_hour)} — {formatHour(c.start_hour + c.duration_hours)}</span>
                {c.is_auto && (
                  <span
                    className="text-h5 font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200"
                    title="Placement automatique — à confirmer depuis le planning atelier"
                  >
                    Auto
                  </span>
                )}
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}

          {unplaced.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                  A planifier
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InterventionsTab({
  interventions,
  interventionGroups,
  atelierCharges,
  onAdd,
  onRemove,
  onToggle,
  onEdit,
}: {
  interventions: Ordre["interventions"];
  interventionGroups?: Ordre["intervention_groups"];
  atelierCharges: AtelierChargeDisplay[];
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
  onToggle: (idx: number) => void;
  onEdit: (idx: number) => void;
}) {
  // Compute additional group keys
  const additionalKeys = useMemo(() => {
    const keys = new Set<string>();
    if (interventionGroups) {
      Object.keys(interventionGroups).filter((k) => k.startsWith("additional_")).forEach((k) => keys.add(k));
    }
    interventions.forEach((i) => { if (i.group?.startsWith("additional_")) keys.add(i.group); });
    // Legacy compat
    if (interventionGroups?.additional || interventions.some((i) => i.group === "additional")) {
      keys.add("additional_0");
    }
    return Array.from(keys).sort();
  }, [interventionGroups, interventions]);

  const initialInterventions = interventions.filter((i) => (i.group || "initial") === "initial");

  const getAdditionalInterventions = (key: string) =>
    interventions.filter((i) =>
      i.group === key || (key === "additional_0" && i.group === "additional"),
    );

  const getGroupStatus = (key: string) => {
    if (!interventionGroups) return undefined;
    return interventionGroups[key] || (key === "additional_0" ? interventionGroups["additional"] : undefined);
  };

  return (
    <div className="flex flex-col p-4 gap-3">
      <ModalGroupSection
        title="Interventions initiales"
        group="initial"
        interventions={initialInterventions}
        groupStatus={interventionGroups?.initial}
        onAdd={onAdd}
        onRemove={(i) => {
          const inter = initialInterventions[i];
          const globalIdx = interventions.findIndex((x) => x.id === inter.id);
          if (globalIdx >= 0) onRemove(globalIdx);
        }}
        onToggle={(i) => {
          const inter = initialInterventions[i];
          const globalIdx = interventions.findIndex((x) => x.id === inter.id);
          if (globalIdx >= 0) onToggle(globalIdx);
        }}
        onEdit={(i) => {
          const inter = initialInterventions[i];
          const globalIdx = interventions.findIndex((x) => x.id === inter.id);
          if (globalIdx >= 0) onEdit(globalIdx);
        }}
      />

      {additionalKeys.map((key, idx) => {
        const groupInterventions = getAdditionalInterventions(key);
        return (
          <ModalGroupSection
            key={key}
            title={`Interventions supplementaires${additionalKeys.length > 1 ? ` #${idx + 1}` : ""}`}
            group={key}
            interventions={groupInterventions}
            groupStatus={getGroupStatus(key)}
            onAdd={onAdd}
            onRemove={(i) => {
              const inter = groupInterventions[i];
              const globalIdx = interventions.findIndex((x) => x.id === inter.id);
              if (globalIdx >= 0) onRemove(globalIdx);
            }}
            onToggle={(i) => {
              const inter = groupInterventions[i];
              const globalIdx = interventions.findIndex((x) => x.id === inter.id);
              if (globalIdx >= 0) onToggle(globalIdx);
            }}
            onEdit={(i) => {
              const inter = groupInterventions[i];
              const globalIdx = interventions.findIndex((x) => x.id === inter.id);
              if (globalIdx >= 0) onEdit(globalIdx);
            }}
          />
        );
      })}

      <AtelierPlanningSection charges={atelierCharges} />
    </div>
  );
}

function ContactsTab({ contacts, onAdd, onEdit, onSetPrimary, onDelete, busyId }: {
  contacts: Ordre["contacts"];
  onAdd: () => void;
  onEdit: (contact: Contact) => void;
  onSetPrimary: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
  busyId: string | null;
}) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <SecondaryButton variant="small" onClick={onAdd}>
          <span className="flex flex-row items-center gap-1">
            <Icon1 name="plus" size={12} />
            Ajouter
          </span>
        </SecondaryButton>
      </div>
      {(contacts || []).map((c, i) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={i} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{displayName}</span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
            {c.is_primary && (
              <span
                className="flex-shrink-0 text-h5 font-medium px-2 py-0.5 rounded-full"
                style={{ background: "#EEF2FF", color: "#4338CA" }}
              >
                Principal
              </span>
            )}
            {!c.is_primary && (
              <button
                onClick={() => onSetPrimary(c)}
                disabled={busyId === c.id}
                className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
                style={{ color: "#6366f1" }}
                title="Faire de ce contact le contact principal du client"
              >
                Principal
              </button>
            )}
            <button
              onClick={() => onEdit(c)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
            {/* Le contact principal ne se supprime pas : on en promeut un autre
                d'abord, sinon le dossier se retrouve sans référent. */}
            <button
              onClick={() => onDelete(c)}
              disabled={c.is_primary || busyId === c.id}
              className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
              title={c.is_primary
                ? "Contact principal : désignez d'abord un autre contact comme principal"
                : "Supprimer ce contact du client"}
              aria-label="Supprimer le contact"
            >
              <Icon1 name="trash-can" size={16} color="#ef4444" />
            </button>
          </div>
        );
      })}
      {(!contacts || contacts.length === 0) && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}
    </div>
  );
}

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" }, { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" }, { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" }, { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

// VehicleEditModal is now imported from @components/modals/VehicleEditModal

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelect,
  onEdit,
}: {
  dossierVehicles: Array<{ id: string; plate?: string; marque?: string; modele?: string; coloris?: string; vin?: string; km?: number; mec?: number }>;
  selectedVehicleId?: string;
  onSelect: (vehicleId: string) => void;
  onEdit: (vehicle: any) => void;
}) {

  return (
    <div className="flex flex-col p-4 gap-2">
      <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Véhicules du dossier</span>
      {dossierVehicles.map((v) => {
        const isSelected = v.id === selectedVehicleId;
        return (
          <div
            key={v.id}
            className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
            style={{
              borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
              background: isSelected ? "#F5F6FF" : "#FFFFFF",
            }}
          >
            <button
              className="flex flex-row items-center gap-3 flex-1 min-w-0 text-left"
              onClick={() => onSelect(v.id)}
            >
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                  background: isSelected ? "#5C6BFF" : "transparent",
                }}
              >
                {isSelected && (
                  <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" iconSize={16} />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate" style={{ fontFamily: "Poppins" }}>
                  {v.marque} {v.modele}
                </span>
                <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                  {v.plate || "—"}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}
                </span>
              </div>
            </button>
            <button
              onClick={() => onEdit(v)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {dossierVehicles.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun véhicule dans le dossier</span>
      )}
    </div>
  );
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

/** Document dates come in two shapes: an ISO timestamp (uploads) or an already
 *  formatted "JJ/MM/AAAA" (chat attachments). Show both as JJ/MM/AAAA. */
function formatDocDate(raw?: string): string {
  if (!raw) return "";
  if (!raw.includes("-")) return raw;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? raw : d.toLocaleDateString("fr-FR");
}

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function PhotoThumbnail({ doc, onClick, onDelete, owner }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void; onDelete?: () => void; owner: DocOwner }) {
  const thumbUrl = useDocumentUrl(doc as ThumbDoc, true, owner);

  return (
    <div className="relative w-[100px] h-[100px] flex-shrink-0 group">
      <div
        className="w-full h-full rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onClick}
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Icon1 name="image" size={20} color="#D4D4D4" />
        )}
      </div>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
          title="Supprimer"
        >
          <Icon1 name="delete" size={13} color="#DC2626" />
        </button>
      )}
    </div>
  );
}


function DocumentsTab({
  documents,
  onAdd,
  onPreview,
  onDelete,
  owner,
  discussionId,
}: {
  documents: Array<{ id?: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
  owner: DocOwner;
  discussionId?: string;
  onAdd: () => void;
  onPreview: (doc: any, gallery?: PreviewGalleryItem[]) => void;
  onDelete: (doc: any) => void;
}) {
  const { onDocContextMenu, menu: sendMenu } = useDocSendMenu(owner, discussionId);
  const photos = documents.filter((d) => d.id && isImageDoc(d as any));
  const files = documents.filter((d) => !isImageDoc(d as any));
  // Même ordre qu'à l'écran : photos puis fichiers.
  const gallery: PreviewGalleryItem[] = [...photos, ...files]
    .filter((d) => d.id && d.gcs_path)
    .map((d) => ({ doc: d as any }));
  const preview = (d: any) => onPreview(d, gallery);

  return (
    <div className="flex flex-col p-4 gap-4">
      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-medium text-neutral-500">Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((p) => (
              <div key={p.id} onContextMenu={onDocContextMenu(p as any)}>
                <PhotoThumbnail doc={p as any} owner={owner} onClick={() => preview(p)} onDelete={p.id ? () => onDelete(p) : undefined} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {photos.length > 0 && <span className="text-h3 font-medium text-neutral-500 mb-1">Documents ({files.length})</span>}
          {files.map((d, i) => (
            <div
              key={d.id ?? i}
              className="flex flex-row items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 cursor-pointer"
              onClick={() => preview(d)}
              onContextMenu={onDocContextMenu(d as any)}
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                <Icon1 name="file" size={14} color="#A1A1A1" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate">{d.name}</span>
                {d.created_at && <span className="text-h4 text-neutral-400">{formatDocDate(d.created_at)}</span>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); preview(d); }} className="p-1 hover:bg-neutral-100 rounded flex-shrink-0">
                <Icon1 name="download" size={14} color="#A1A1A1" />
              </button>
              {d.id && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(d); }} className="p-1 hover:bg-red-50 rounded flex-shrink-0" title="Supprimer">
                  <Icon1 name="delete" size={14} color="#DC2626" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun document</span>
      )}
      {sendMenu}

      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
        onClick={onAdd}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un document
      </button>
    </div>
  );
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}

        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Main OrdreModal ───────────────────────────────────────

interface OrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
  onSave: (ordre: Ordre) => void;
  onDelete?: (ordreId: string) => void;
}

export default function OrdreModal({ ordre, onClose, onSave, onDelete }: OrdreModalProps) {
  const [activeTab, setActiveTab] = useState("ordre");
  // Pastille on the Documents tab when a client sent a file (WhatsApp). Cleared
  // locally + server-side the first time staff opens the Documents tab.
  const [docsSeen, setDocsSeen] = useState(false);
  const showDocsBadge = !!ordre.has_unseen_client_docs && !docsSeen;
  const handleSetActiveTab = (t: string) => {
    setActiveTab(t);
    if (t === "documents" && showDocsBadge) {
      setDocsSeen(true);
      ordresApi.update(ordre.id, { has_unseen_client_docs: false }).catch(() => {});
    }
  };
  const tabsWithBadge = TABS.map((t) =>
    t.value === "documents" && showDocsBadge ? { ...t, badge: 1, badgeColor: "#6366f1" } : t,
  );
  const [form, setForm] = useState<Ordre>({ ...ordre });
  // The version ServiceBox and Carvis last confirmed. « Enregistrer » no longer closes the
  // modal, so "what changed?" must be measured against the last save and not against the
  // ordre the screen opened with — otherwise a second save re-asks to prévenir le client
  // about a date change already sent, and re-proposes to shrink an atelier block already
  // shrunk. The host screen may or may not feed a fresh prop; this does not depend on it.
  const [savedOrdre, setSavedOrdre] = useState<Ordre>(ordre);
  const [subModal, setSubModal] = useState<string | null>(null);
  // Index de la ligne en cours de correction — rendue au même niveau que la modale
  // d'ajout, pour qu'elle passe au-dessus de la modale ordre comme elle.
  const [editingIntervention, setEditingIntervention] = useState<number | null>(null);
  const [shrinkPlan, setShrinkPlan] = useState<ShrinkPlan | null>(null);
  const [addGroup, setAddGroup] = useState<string>("initial");
  const [showRestitutionConfirm, setShowRestitutionConfirm] = useState(false);
  const [forceConfirm, setForceConfirm] = useState<SlotValidationResult | null>(null);
  // Remembers that the user chose to force a non-conforming slot, so the force
  // intent survives the "notify client" confirm modal and reaches handleSave.
  const [pendingForce, setPendingForce] = useState(false);
  // ServiceBox slot-collision prediction: does the reception/restitution slot land
  // on a reception column already taken by another RDV? (transfer would be refused)
  const [receptionConflict, setReceptionConflict] = useState<ReceptionConflictResult | null>(null);
  // Occupied slots on the ordre's reception columns — greyed out in the date pickers.
  const [recDisabledSlots, setRecDisabledSlots] = useState<string[]>([]);
  const [restDisabledSlots, setRestDisabledSlots] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInServiceBox, setDeleteInServiceBox] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showAutoPlace, setShowAutoPlace] = useState<"results" | null>(null);
  // Interventions ajoutées avec « Planifier automatiquement » : posées sur le
  // planning atelier au prochain enregistrement, sans question.
  const [pendingAutoPlace, setPendingAutoPlace] = useState<string[]>([]);
  const [autoPlaceResults, setAutoPlaceResults] = useState<{
    placed: { intervention_id: string; label: string; worker_name: string; date?: string; start_hour?: number; duration_hours?: number }[];
    unplaced: { intervention_id: string; label: string; worker_name: string; reason?: string }[];
  } | null>(null);

  const update = <K extends keyof Ordre>(key: K, value: Ordre[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  // « Envoyer au client » depuis l'onglet Documents : la conversation est dans un autre
  // onglet de la modale, on y va ; son ChatPanel pose alors le document dans la saisie.
  const docStagedForChat = useChatStageStore((s) => {
    const d = parentDossier?.discussion_id || ordre.discussion_id;
    return !!d && s.pending?.discussionId === d;
  });
  useEffect(() => { if (docStagedForChat) setActiveTab("communications"); }, [docStagedForChat]);
  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  // Recall campaigns: seed from the ordre's embedded list, then merge what's stored
  // in the recall_campaigns collection (the OR fiche shows these — keep parity).
  const [recalls, setRecalls] = useState<{ code: string; libelle: string; status: string }[]>(
    (ordre.recall_campaigns || []).map((c) => ({ code: c.code, libelle: c.libelle, status: c.status || "detected" })),
  );
  useEffect(() => {
    recallCampaignsApi.listForOrdre(ordre.id)
      .then((stored) => {
        setRecalls((prev) => {
          const byCode = new Map(prev.map((c) => [c.code || c.libelle, c]));
          stored.forEach((s) => {
            const key = s.campaign_code || s.campaign_title;
            if (!byCode.has(key)) byCode.set(key, { code: s.campaign_code, libelle: s.campaign_title, status: s.status });
          });
          return Array.from(byCode.values());
        });
      })
      .catch(() => {});
  }, [ordre.id]);

  const orgData = useDataStore((s) => s.orgData);
  const operatorEmail = useDataStore((s) => s.userData?.infos?.email) || "";
  const orgMembers = useDataStore((s) => s.orgMembers);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);
  // Jobs for THIS ordre, fetched by ordre_id (not week-scoped like the store). The atelier
  // store only loads jobs for the currently-selected planning week, so a placement on a future
  // week would be invisible here — this dedicated fetch makes "Planification atelier" reliable
  // regardless of where the placement lands in time.
  const [ordreJobs, setOrdreJobs] = useState<WorkshopJob[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
      atelierApi.listJobs({ org_id: orgData.id, ordre_id: ordre.id, limit: 1000 }).then(setOrdreJobs).catch(() => {});
    }
  }, [orgData?.id, ordre.id]);

  // ── Actions menu state (parity with the Ordre detail page) ──
  const [payments, setPayments] = useState<Payment[]>([]);
  const [rentalVehicle, setRentalVehicle] = useState<Vehicle | null>(null);
  const [showEditRentalRes, setShowEditRentalRes] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  // Un ordre peut enchaîner plusieurs mobilités (prêt puis location) — même liste que la
  // fiche ordre, les deux vues restent jumelles.
  const { mobilities, reloadMobilities } = useOrdreMobilities(ordre.id);
  const [editMobilityId, setEditMobilityId] = useState<string | null>(null);
  const [showContratModal, setShowContratModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<"acompte" | "solde" | null>(null);
  const [showFactureUpload, setShowFactureUpload] = useState(false);
  const [showTrackingPreview, setShowTrackingPreview] = useState(false);
  const [showServiceBoxModal, setShowServiceBoxModal] = useState(false);
  const [showSbInterventions, setShowSbInterventions] = useState(false);
  const [showSbHistory, setShowSbHistory] = useState(false);
  // Fields persisted on the ordre after a successful ServiceBox transfer. The `ordre`
  // prop here is fed by a non-store-subscribed host (planning), so we overlay these
  // locally to refresh the N° OR field and the Actions menu without reopening the modal.
  const [transferUpdates, setTransferUpdates] = useState<Partial<Ordre> | null>(null);
  const currentOrdre = transferUpdates ? ({ ...savedOrdre, ...transferUpdates } as Ordre) : savedOrdre;
  // An OR Alpha hands back to a job started from a sub-modal (« Mettre à jour ServiceBox »)
  // lands in the store, not in this prop: without this, « Récupérer devis » stayed greyed
  // until the page was reloaded (30/09/2026).
  const alphaOrNumber = useAlphaTransferStore((st) => {
    const job = st.jobs[savedOrdre.id];
    return job?.status === "done" ? job.orNumber : undefined;
  });
  useEffect(() => {
    if (alphaOrNumber && alphaOrNumber !== currentOrdre.or_number) {
      setTransferUpdates((prev) => ({ ...(prev || {}), or_number: alphaOrNumber, num: `OR-${alphaOrNumber}` }));
    }
  }, [alphaOrNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showServiceBoxEstimation, setShowServiceBoxEstimation] = useState(false);
  const [sbExtraction, setSbExtraction] = useState<EstimateExtraction | null>(null);
  const [sbEstimationLoading, setSbEstimationLoading] = useState(false);
  // Forfait variant picker (when the DMS proposes several forfaits for a code).
  const [sbForfaitChoices, setSbForfaitChoices] = useState<ForfaitChoice[] | null>(null);
  const [sbForfaitSel, setSbForfaitSel] = useState<Record<string, string>>({});
  const [sbForfaitSubmitting, setSbForfaitSubmitting] = useState(false);
  // « Remplacée / remplaçante » picker (a part of the panier has a newer reference).
  const [sbPrChoices, setSbPrChoices] = useState<PrChoice[] | null>(null);
  const [serviceboxProxyAvailable, setServiceboxProxyAvailable] = useState(false);
  const [flagReasonModal, setFlagReasonModal] = useState<{ flag: OrdreFlag; newFlags: OrdreFlag[] } | null>(null);
  const [flagReasonText, setFlagReasonText] = useState("");

  const hasFacture = useMemo(
    () => (ordre.documents || []).some((d) => d.type?.toLowerCase().replace(" ", "_") === "facture"),
    [ordre.documents],
  );
  const primaryVehicle = ordre.vehicle || (ordre.vehicle_id ? parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id) : undefined);

  const fetchPayments = () => { paymentsApi.listForOrdre(ordre.id).then(setPayments).catch(() => {}); };

  useEffect(() => { fetchPayments(); }, [ordre.id]);

  useEffect(() => {
    if (ordre.rental_vehicle_id) {
      vehiclesApi.get(ordre.rental_vehicle_id).then(setRentalVehicle).catch(() => {});
    } else {
      setRentalVehicle(null);
    }
  }, [ordre.rental_vehicle_id]);

  useEffect(() => {
    if (!orgData?.servicebox?.enabled) return;
    let cancelled = false;
    serviceboxProxy.checkHealth().then((r) => { if (!cancelled) setServiceboxProxyAvailable(!!r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [orgData?.servicebox?.enabled]);

  // operator_email is not decoration: it is what separates a wait someone sat through
  // from the proxy's own traffic on the « Délais SB » dashboard.
  const sbCreds = () => ({ username: orgData?.servicebox?.username || "", password: orgData?.servicebox?.password || "", site_code: orgData?.servicebox?.site_code, operator_email: operatorEmail });

  // The `ordre` prop can be stale (the planning host isn't store-subscribed), so a
  // ServiceBox dossier id set by an earlier transfer may be missing here even though
  // it exists on the backend — which is why this works on the Ordre detail page (fresh
  // store data) but errors from the modal. Resolve from the local overlay first, then
  // re-fetch as a self-healing fallback (also refreshes the store and the overlay).
  const resolveDossierId = async (): Promise<string | undefined> => {
    if (currentOrdre.servicebox_dossier_id) return currentOrdre.servicebox_dossier_id;
    try {
      const fresh = await ordresApi.get(ordre.id);
      useOrdreStore.getState().updateOrdre(fresh);
      setTransferUpdates((prev) => ({ ...(prev || {}), servicebox_dossier_id: fresh.servicebox_dossier_id, servicebox_rdv_id: fresh.servicebox_rdv_id }));
      return fresh.servicebox_dossier_id;
    } catch {
      return undefined;
    }
  };

  // When ServiceBox waits for a choice (forfait variant, or replaced part), open the
  // matching picker. Returns true if handled.
  const handleForfaitChoiceResult = (result: EstimationResult) => {
    if (result.needs_pr_choice && result.pr_choices?.length) {
      setSbForfaitChoices(null);
      setSbPrChoices(result.pr_choices);
      setShowServiceBoxEstimation(false);
      return true;
    }
    if (!result.needs_forfait_choice) return false;
    const choices = result.forfait_choices || [];
    const sel: Record<string, string> = {};
    for (const c of choices) {
      if (!(c.field in sel) || c.selected) sel[c.field] = c.code;
    }
    setSbForfaitChoices(choices);
    setSbForfaitSel(sel);
    setShowServiceBoxEstimation(false);
    return true;
  };

  // Le serveur réécrit interventions/documents/statut dans notre dos (import et
  // validation du devis, OR-1186366) : on adopte sa version pour chaque champ que
  // l'utilisateur n'a pas touché ici, sinon la modale affiche — et un « Enregistrer »
  // renverrait — les lignes d'avant le devis.
  const adoptServerOrdre = (fresh: Ordre) => {
    const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    setForm((prev) => {
      const next = { ...prev } as any;
      for (const k of SERVER_OWNED_FIELDS) {
        if (same((prev as any)[k], (savedOrdre as any)[k])) next[k] = (fresh as any)[k];
      }
      return next;
    });
    setSavedOrdre(fresh);
  };

  const refreshFromServer = async () => {
    try {
      const refreshed = await ordresApi.get(ordre.id);
      useOrdreStore.getState().updateOrdre(refreshed);
      adoptServerOrdre(refreshed);
    } catch {
      /* le prochain enregistrement n'écrase de toute façon plus ces champs */
    }
  };

  const importEstimationHtml = async (html: string) => {
    const extraction = await estimateExtractionApi.importServicebox(ordre.id, html);
    setSbExtraction(extraction);
    await refreshFromServer();
  };

  const submitForfaitChoice = async () => {
    const dossierId = currentOrdre.servicebox_dossier_id;
    if (!orgData?.servicebox || !dossierId) return;
    setSbForfaitSubmitting(true);
    try {
      const selections = Object.entries(sbForfaitSel).map(([field, value]) => ({ field, value }));
      const result = await serviceboxProxy.chooseForfait(sbCreds(), dossierId, selections, busyNotifier());
      if (handleForfaitChoiceResult(result)) return; // still ambiguous (another group)
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer le devis" });
        return;
      }
      setSbForfaitChoices(null);
      setShowServiceBoxEstimation(true);
      setSbEstimationLoading(true);
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
    } finally {
      setSbForfaitSubmitting(false);
      setSbEstimationLoading(false);
    }
  };

  const handlePrChoiceResult = async (result: EstimationResult) => {
    if (handleForfaitChoiceResult(result)) return; // another choice still pending
    if (!result.success || !result.html) {
      showNotification({ type: "error", message: result.error || "Impossible de récupérer le devis" });
      return;
    }
    setSbPrChoices(null);
    setShowServiceBoxEstimation(true);
    setSbEstimationLoading(true);
    try {
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
      setShowServiceBoxEstimation(false);
    } finally {
      setSbEstimationLoading(false);
    }
  };

  /** The devis fetch is also when the numéro d'OR can be read back: the garage transfers
   *  the dossier to Alpha itself (Carvis never does — Alpha prints the priced CT the
   *  moment the OR is created), and Alpha writes the number onto the RDV. Fire-and-forget
   *  next to the estimation, whose lines and prices are the point of the trip. */
  const captureOrNumber = () => {
    if (ordre.or_number) return;
    serviceboxSyncApi
      .refreshOrNumber(ordre.id)
      .then((res) => {
        if (!res.updated || !res.or_number) return;
        useOrdreStore.getState().updateOrdre({ ...ordre, or_number: res.or_number, num: `OR-${res.or_number}` } as any);
        showNotification({ type: "success", message: `Numéro d'OR récupéré : OR-${res.or_number}` });
      })
      .catch(() => {});
  };

  const handleImportServiceBoxEstimation = async () => {
    if (!orgData?.servicebox) return;
    const dossierId = await resolveDossierId();
    if (!dossierId) {
      showNotification({ type: "error", message: "Aucun dossier ServiceBox lié à cet OR. Transférez d'abord vers ServiceBox." });
      return;
    }
    setSbExtraction(null);
    setShowServiceBoxEstimation(true);
    setSbEstimationLoading(true);
    try {
      const result = await serviceboxProxy.fetchEstimation(sbCreds(), dossierId, busyNotifier());
      if (handleForfaitChoiceResult(result)) return;
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer l'estimation" });
        setShowServiceBoxEstimation(false);
        return;
      }
      await importEstimationHtml(result.html);
      captureOrNumber();
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
      setShowServiceBoxEstimation(false);
    } finally {
      setSbEstimationLoading(false);
    }
  };

  const ordreAtelierCharges = useMemo(() => {
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    // Placed jobs for this ordre. Merge the realtime store (fresh confirm/move updates, but
    // only for the currently-loaded week) with the dedicated ordre_id fetch (any week). Store
    // wins on id collision so a just-confirmed job reflects immediately.
    const jobById = new Map<string, WorkshopJob>();
    ordreJobs.filter((j) => j.ordre_id === ordre.id).forEach((j) => jobById.set(j.id, j));
    atelierJobs.filter((j) => j.ordre_id === ordre.id).forEach((j) => jobById.set(j.id, j));
    const jobRows: AtelierChargeDisplay[] = Array.from(jobById.values()).map((j) => ({
      id: j.id,
      worker_name: workerMap.get(j.workerId) || "—",
      specialty: j.specialty,
      date: j.date,
      start_hour: j.startHour,
      duration_hours: j.durationHours,
      is_placed: true,
      is_auto: !!j.autoGenerated,
    }));
    // Unplaced charges (still in sidebar)
    const chargeRows: AtelierChargeDisplay[] = atelierCharges
      .filter((c) => c.ordre_id === ordre.id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigne",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
        is_placed: false,
        is_auto: !!c.autoGenerated,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, ordreJobs, atelierWorkers, atelierCharges, ordre.id]);

  // Display-only merge: people assigned via the dropdown (form.assigned_to_ids)
  // unioned with mechanics scheduled in the atelier planning, deduped by member
  // id (atelier worker.id === org member id). Uses `form` so chips react live to
  // dropdown edits before save.
  const peopleOnOrdre = useMemo(() => {
    const assignedIds = getAssignedIds(form);
    const scheduledIds = new Set<string>();
    const scheduledOrder: string[] = [];
    for (const j of atelierJobs) {
      if (j.ordre_id === ordre.id && j.workerId && !scheduledIds.has(j.workerId)) {
        scheduledIds.add(j.workerId);
        scheduledOrder.push(j.workerId);
      }
    }
    const order: string[] = [];
    const seen = new Set<string>();
    for (const pid of assignedIds.concat(scheduledOrder)) {
      if (pid && !seen.has(pid)) { seen.add(pid); order.push(pid); }
    }
    return order.map((pid) => {
      const w = atelierWorkers.find((ww) => ww.id === pid);
      const m = orgMembers.find((mm) => mm.id === pid);
      const name = w?.name || (m ? (m.infos.display_name || m.infos.full_name) : pid) || "?";
      const specialty = w?.specialty || (m?.config as any)?.team || "autre";
      return {
        id: pid,
        name,
        specialty,
        assigned: assignedIds.includes(pid),
        scheduled: scheduledIds.has(pid),
      };
    });
  }, [form, atelierJobs, atelierWorkers, orgMembers, ordre.id]);

  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactBusyId, setContactBusyId] = useState<string | null>(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [pendingContactData, setPendingContactData] = useState<{ contact: Contact | null; data: Partial<Contact> } | null>(null);
  // Auto-push a primary-contact client edit to ServiceBox when this ordre is already transferred.
  const [sbAutoUpdateOrdre, setSbAutoUpdateOrdre] = useState<Ordre | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<ClientVehicle | null>(null);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: any; url: string; gallery?: PreviewGalleryItem[] } | null>(null);
  const [docToDelete, setDocToDelete] = useState<{ id: string; name: string; gcs_path?: string } | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  // Onglet Documents : ceux de l'ordre (devis, devis signé…) PLUS ceux du dossier
  // (carte grise…). Avant, le dossier remplaçait l'ordre dès qu'il avait un seul
  // document → OR-1187572 : carte grise visible, devis initial et signé introuvables.
  // Même fusion que OrdreDetailScreen.
  const mergedDocuments = useMemo(() => {
    const seen = new Set<string>();
    const merged: NonNullable<typeof form.documents> = [];
    for (const d of [...(form.documents || []), ...(parentDossier?.documents || [])]) {
      const key = d.id || d.gcs_path || d.name;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(d);
    }
    return merged;
  }, [form.documents, parentDossier?.documents]);

  const handleDeleteDocument = async () => {
    if (!docToDelete?.id) return;
    setDeletingDoc(true);
    try {
      // Appartenance réelle, pas le préfixe du chemin : le devis signé vit sous
      // « estimate_extractions/ » mais il est rangé sur l'ordre.
      const isOrdreDoc = (form.documents || []).some((d) => d.id === docToDelete.id)
        || !!docToDelete.gcs_path?.startsWith("ordres/");
      if (isOrdreDoc) {
        const updated = await ordresApi.deleteDocument(ordre.id, docToDelete.id);
        setForm((prev) => ({ ...prev, documents: updated.documents }));
        useOrdreStore.getState().updateOrdre(updated);
      } else if (parentDossier) {
        const updated = await dossiersApi.deleteDocument(parentDossier.id, docToDelete.id);
        setParentDossier(updated);
      }
      showNotification({ type: "success", message: "Document supprimé" });
      setDocToDelete(null);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression du document" });
    } finally {
      setDeletingDoc(false);
    }
  };

  const openDocPreview = async (doc: { id?: string; gcs_path?: string; name: string }, gallery?: PreviewGalleryItem[]) => {
    if (!doc.id || !doc.gcs_path) return;
    try {
      const url = await resolveDocumentUrl({ id: doc.id, gcs_path: doc.gcs_path }, { ordreId: ordre.id, dossierId: parentDossier?.id });
      setPreviewDoc({ doc, url, gallery });
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    }
  };

  const saveContactToDossier = async (contact: Contact | null, data: Partial<Contact>) => {
    if (!parentDossier) return;
    setContactSaving(true);
    // Le principal change de coordonnées, ou un autre contact devient le principal : le
    // client vu par ServiceBox change dans les deux cas.
    const promoted = !!data.is_primary && !(contact?.is_primary);
    const primaryClientChanged = promoted || (!!contact && contact.is_primary &&
      (["adresse", "nom", "prenom", "phone", "email", "civilite"] as const).some(
        (k) => data[k] !== undefined && data[k] !== contact[k],
      ));
    try {
      let updatedContacts: Contact[];
      if (contact) {
        // Editing existing contact — un seul principal à la fois.
        updatedContacts = withSinglePrimary(
          (parentDossier.contacts || []).map((c) => (c.id === contact.id ? { ...c, ...data } : c)),
          data.is_primary ? contact.id : undefined,
        );
      } else {
        // Adding new contact
        const newContact: Contact = {
          id: `c-new-${Date.now()}`,
          dossier_id: parentDossier.id,
          nom: data.nom || "",
          is_primary: data.is_primary || false,
          created_at: new Date().toISOString(),
          ...data,
        };
        updatedContacts = withSinglePrimary(
          [...(parentDossier.contacts || []), newContact],
          newContact.is_primary ? newContact.id : undefined,
        );
      }
      const updatedDossier = await dossiersApi.update(parentDossier.id, { contacts: updatedContacts } as any);
      setParentDossier(updatedDossier);
      // Re-fetch enriched ordre to update form.contacts
      const refreshedOrdre = await ordresApi.get(ordre.id);
      setForm((prev) => ({ ...prev, contacts: refreshedOrdre.contacts }));
      useOrdreStore.getState().updateOrdre(refreshedOrdre);
      setSubModal(null);
      setEditingContact(null);
      setPendingContactData(null);
      showNotification({ type: "success", message: contact ? "Contact mis à jour" : "Contact ajouté" });
      // Already transferred → push the updated client to its ServiceBox RDV.
      if (primaryClientChanged && orgData?.servicebox?.enabled && refreshedOrdre.servicebox_rdv_id) {
        setSbAutoUpdateOrdre(refreshedOrdre);
      }
      // ServiceBox suit, Alpha non : une fois l'OR créé, rien ne lui renvoie le client.
      const alphaNotice = primaryClientChanged ? alphaClientNotUpdatedNotice(refreshedOrdre) : null;
      if (alphaNotice) showNotification({ type: "warning", message: alphaNotice });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement du contact" });
    }
    setContactSaving(false);
  };

  // Le contact principal est une propriété du CLIENT, pas de l'ordre : les deux actions
  // ci-dessous écrivent donc sur le dossier, comme le fait déjà l'édition d'un contact.
  const writeDossierContacts = async (updatedContacts: Contact[], message: string) => {
    if (!parentDossier) return;
    try {
      const updatedDossier = await dossiersApi.update(parentDossier.id, { contacts: updatedContacts } as any);
      setParentDossier(updatedDossier);
      const refreshedOrdre = await ordresApi.get(ordre.id);
      setForm((prev) => ({ ...prev, contacts: refreshedOrdre.contacts }));
      useOrdreStore.getState().updateOrdre(refreshedOrdre);
      showNotification({ type: "success", message });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement" });
    }
  };

  const setPrimaryContact = async (contact: Contact) => {
    if (!parentDossier) return;
    setContactBusyId(contact.id);
    await writeDossierContacts(
      (parentDossier.contacts || []).map((c) => ({ ...c, is_primary: c.id === contact.id })),
      "Contact principal mis à jour",
    );
    setContactBusyId(null);
  };

  const deleteContact = async (contact: Contact) => {
    if (!parentDossier || contact.is_primary) return;
    const name = [contact.prenom, contact.nom].filter(Boolean).join(" ") || "ce contact";
    // Le contact appartient au client : il disparaît aussi des autres ordres.
    if (!window.confirm(`Supprimer ${name} ? Le contact est retiré du client, donc de tous ses ordres.`)) return;
    setContactBusyId(contact.id);
    await writeDossierContacts(
      (parentDossier.contacts || []).filter((c) => c.id !== contact.id),
      "Contact supprimé",
    );
    setContactBusyId(null);
  };

  const handleContactSave = (contact: Contact | null, data: Partial<Contact>) => {
    const isPrimaryContact = contact?.is_primary || data.is_primary;
    if (isPrimaryContact && contact) {
      const emailChanged = data.email !== undefined && data.email !== contact.email;
      const phoneChanged = data.phone !== undefined && data.phone !== contact.phone;
      // Un autre contact devient le principal : le client change, même confirmation.
      const promoted = !!data.is_primary && !contact.is_primary;
      if (emailChanged || phoneChanged || promoted) {
        setPendingContactData({ contact, data });
        return;
      }
    }
    if (!contact && data.is_primary && (data.email || data.phone)) {
      setPendingContactData({ contact, data });
      return;
    }
    saveContactToDossier(contact, data);
  };

  const saveVehicleToDossier = async (vehicle: ClientVehicle, data: Partial<ClientVehicle>) => {
    if (!parentDossier) return;
    setVehicleSaving(true);
    try {
      const updatedVehicles = (parentDossier.vehicles || []).map((v) =>
        v.id === vehicle.id ? { ...v, ...data } : v
      );
      const updatedDossier = await dossiersApi.update(parentDossier.id, { vehicles: updatedVehicles } as any);
      setParentDossier(updatedDossier);
      try { await clientVehiclesApi.update(vehicle.id, data); } catch { /* shared client_vehicles copy, best-effort */ }
      // If this is the selected vehicle, update the form too
      if (form.vehicle_id === vehicle.id || form.vehicle?.id === vehicle.id) {
        const updated = updatedVehicles.find((v) => v.id === vehicle.id);
        if (updated) update("vehicle", updated as any);
      }
      setEditingVehicle(null);
      showNotification({ type: "success", message: "Véhicule modifié" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde du véhicule" });
    } finally {
      setVehicleSaving(false);
    }
  };

  const restitutionChanged =
    form.restitution?.seconds !== savedOrdre.restitution?.seconds;
  const receptionChanged =
    form.reception_date?.seconds !== savedOrdre.reception_date?.seconds;
  // A change worth telling the client about only exists when there was a PRIOR
  // date. Setting a date for the first time (e.g. a fresh RDV) is not a change,
  // so it must not pop the "prévenir le client" confirmation.
  const restitutionNotifiable = !!savedOrdre.restitution?.seconds && restitutionChanged;
  const receptionNotifiable = !!savedOrdre.reception_date?.seconds && receptionChanged;

  // ServiceBox slot-collision check: immediate on open, debounced on later edits.
  const conflictFirstRun = useRef(true);
  useEffect(() => {
    if (!ordre.id) { setReceptionConflict(null); return; }
    const rec = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds) : null;
    const rest = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds) : null;
    if (!rec && !rest) { setReceptionConflict(null); return; }
    let cancelled = false;
    const run = () => {
      appointmentsApi
        .checkReceptionConflicts({
          ordre_id: ordre.id,
          reception_date: rec?.date,
          reception_time: rec?.time,
          restitution_date: rest?.date,
          restitution_time: rest?.time,
        })
        .then((res) => { if (!cancelled) setReceptionConflict(res); })
        .catch((e) => console.error("reception conflict check failed", e));
    };
    // Run immediately the first time (on open), debounce subsequent date edits.
    if (conflictFirstRun.current) {
      conflictFirstRun.current = false;
      run();
      return;
    }
    const t = setTimeout(run, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ordre.id, form.reception_date?.seconds, form.restitution?.seconds]);

  // Occupied slots on the reception/restitution columns for the chosen day → grey them out.
  const recDateStr = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds).date : null;
  const restDateStr = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds).date : null;
  useEffect(() => {
    if (!ordre.id || !recDateStr) { setRecDisabledSlots([]); return; }
    let cancelled = false;
    appointmentsApi.occupiedSlots({ ordre_id: ordre.id, type: "reception", date: recDateStr })
      .then((s) => { if (!cancelled) setRecDisabledSlots(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ordre.id, recDateStr]);
  useEffect(() => {
    if (!ordre.id || !restDateStr) { setRestDisabledSlots([]); return; }
    let cancelled = false;
    appointmentsApi.occupiedSlots({ ordre_id: ordre.id, type: "restitution", date: restDateStr })
      .then((s) => { if (!cancelled) setRestDisabledSlots(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ordre.id, restDateStr]);

  /** Move a colliding event's RDV to the chosen free reception column. */
  const switchReception = async (ev: ReceptionConflictEvent, recId: string) => {
    const free = ev.free_receptions.find((r) => r.id === recId);
    if (!free || !ev.appointment_id) return;
    try {
      await appointmentsApi.update(ev.appointment_id, { category: free.id });
      showNotification({ type: "success", message: `Basculé sur ${free.label}` });
      const ps = usePlanningStore.getState();
      if (ps._fetchedOrgId) ps.fetchAppointments(ps._fetchedOrgId, true);
      const rec = form.reception_date?.seconds ? secondsToDateTime(form.reception_date.seconds) : null;
      const rest = form.restitution?.seconds ? secondsToDateTime(form.restitution.seconds) : null;
      const res = await appointmentsApi.checkReceptionConflicts({
        ordre_id: ordre.id,
        reception_date: rec?.date,
        reception_time: rec?.time,
        restitution_date: rest?.date,
        restitution_time: rest?.time,
      });
      setReceptionConflict(res);
    } catch (e) {
      showNotification({ type: "error", message: "Échec du changement de réception" });
    }
  };

  const handleSave = async (restitutionComment?: string, notify?: boolean, force?: boolean) => {
    setSaving(true);
    try {
      // Champs que le serveur fait aussi évoluer : envoyés seulement s'ils ont été
      // modifiés ici. Les renvoyer tels quels écrasait le devis validé entre-temps
      // (OR-1186366 : documents vidés, lignes revenues à 1 h, charge perdue).
      const dirty = (k: (typeof SERVER_OWNED_FIELDS)[number]) =>
        JSON.stringify((form as any)[k] ?? null) !== JSON.stringify((savedOrdre as any)[k] ?? null);
      const updated = await ordresApi.update(ordre.id, {
        name: form.name,
        tags: form.tags,
        tag_ids: form.tag_ids,
        arret_reason: form.arret_reason,
        ...(dirty("status") ? { status: form.status } : {}),
        immatriculation: form.immatriculation,
        modele: form.modele,
        campagne: form.campagne,
        assigned_to_ids: form.assigned_to_ids,
        notes: form.notes,
        ...(dirty("interventions") ? { interventions: form.interventions } : {}),
        ...(dirty("documents") ? { documents: form.documents } : {}),
        reception_date: form.reception_date,
        restitution: form.restitution,
        vehicle_id: form.vehicle_id,
        ...(force ? { force: true } : {}),
      });
      // If restitution date changed, notify client (only if checkbox was checked)
      if (restitutionNotifiable && form.restitution?.seconds && notify !== false) {
        try {
          await ordresApi.notifyRestitutionChange(ordre.id, {
            old_date_seconds: savedOrdre.restitution?.seconds,
            new_date_seconds: form.restitution.seconds,
            comment: restitutionComment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying restitution change:", e);
        }
      }
      // If reception (drop-off) time changed, notify client too
      if (receptionNotifiable && form.reception_date?.seconds && notify !== false) {
        try {
          await ordresApi.notifyReceptionChange(ordre.id, {
            old_date_seconds: savedOrdre.reception_date?.seconds,
            new_date_seconds: form.reception_date.seconds,
            comment: restitutionComment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying reception change:", e);
        }
      }
      useOrdreStore.getState().updateOrdre(updated);
      // Refetch planning appointments so the grid reflects updated times
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId, true);
      }
      // Invalidate cached car windows so atelier grid picks up new dates
      useAtelierStore.getState().invalidateCarWindows(ordre.id);
      // Saving keeps the modal open (adding an intervention then pushing it to
      // ServiceBox needs both, and closing sent the user back to the planning). The host is
      // still told, so its own copy follows; it no longer reads the call as "close me".
      setSavedOrdre(updated);
      setForm({ ...updated });
      onSave(updated);
      showNotification({ type: "success", message: "Ordre enregistré" });

      // Le travail a-t-il diminué sous ce qui est déjà posé en atelier ? Les blocs
      // ne se raccourcissent jamais d'eux-mêmes ; on propose de libérer le surplus.
      const previouslyNeeded = (savedOrdre.interventions || [])
        .filter((i) => !isAPrevoir(i))
        .reduce((sum, i) => sum + (i.estimated_hours || 0), 0);
      const plan = planAtelierShrink(ordre.id, form.interventions);
      if (plan.needed < previouslyNeeded && plan.adjustments.length > 0) setShrinkPlan(plan);

      // Planif auto choisie à l'ajout de l'intervention. On ne demandait plus rien
      // d'utile : la question revenait à CHAQUE enregistrement dès qu'une
      // intervention était assignée, « Non merci » ou pas.
      // Jamais pour un devis complémentaire : il se répartit à la main depuis le planning.
      const toPlace = form.interventions.filter((i) => i.assigned_to && isInitialGroup(i.group) && pendingAutoPlace.includes(i.id));
      setPendingAutoPlace([]);
      if (toPlace.length > 0) handleAutoPlace(toPlace);
    } catch (e: any) {
      console.error("Error saving ordre:", e);
      // Le back peut refuser la fenetre reception->restitution alors que
      // l'utilisateur n'y a pas touche (une intervention ajoutee repousse l'heure
      // minimale). Un « Erreur lors de l'enregistrement » ne disait ni pourquoi ni
      // comment s'en sortir : on montre le motif du serveur et on propose de forcer.
      const detail = e?.response?.data?.detail;
      // 409 = refus que « Forcer » ne lève pas (voiture présente moins longtemps que
      // les travaux, garde du prêt…) : on dit pourquoi, sans proposer de forcer.
      const unforceable = e?.response?.status === 409;
      if (detail && (!force || unforceable)) {
        setForceConfirm({
          errors: [{ code: "server", message: String(detail), forceable: !unforceable }],
          suggested_restitution_time: null,
          suggested_restitution_date: null,
          earliest_restitution_hour: null,
        });
      } else {
        showNotification({ type: "error", message: detail ? String(detail) : "Erreur lors de l'enregistrement" });
      }
    } finally {
      setSaving(false);
      setShowRestitutionConfirm(false);
      setPendingForce(false);
    }
  };

  /** Continue the normal save path (restitution-change confirm, or direct save). */
  const proceedSave = (force = false) => {
    // Only ask to notify the client when an EXISTING date actually changed.
    if ((restitutionNotifiable && form.restitution?.seconds) || (receptionNotifiable && form.reception_date?.seconds)) {
      setPendingForce(force);
      setShowRestitutionConfirm(true);
    } else {
      handleSave(undefined, undefined, force);
    }
  };

  const handleSaveClick = async () => {
    const recSec = form.reception_date?.seconds;
    // Validate the slot with the SAME rules as the online widget when dates change
    if ((receptionChanged || restitutionChanged) && recSec) {
      try {
        const dep = secondsToDateTime(recSec);
        const restSec = form.restitution?.seconds;
        const rest = restSec ? secondsToDateTime(restSec) : { date: null, time: null };
        const interventionHours = form.interventions.filter((i) => !isAPrevoir(i)).reduce(
          (sum, i) => sum + (i.estimated_hours || 1.5), 0,
        ) || null;
        // Same sum as the server's non-forceable rule (rdv_presence.ordre_work_hours).
        const workHours = form.interventions
          .filter((i) => !isAPrevoir(i) && !["refuse", "refused", "refusee"].includes((i as any).status || ""))
          .reduce((sum, i) => sum + (i.estimated_hours || 0), 0);
        const result = await bookingsApi.validateSlot({
          deposit_date: dep.date,
          deposit_time: dep.time,
          service_type: form.campagne || (form.booking_request as any)?.service || "Mécanique",
          intervention_hours: interventionHours,
          work_hours: workHours,
          restitution_date: rest.date,
          restitution_time: rest.time,
          check_deposit: true,
          exclude_ordre_id: ordre.id,
        });
        if (result.errors.length > 0) {
          setForceConfirm(result);
          return;
        }
      } catch (e) {
        // Fail-open: if validation is unreachable, don't block the staff save
        console.error("validateSlot failed:", e);
      }
    }
    proceedSave();
  };

  /** Apply the validator's suggested restitution time on the reception day. */
  const applySuggestedRestitution = (time: string) => {
    const recSec = form.reception_date?.seconds;
    if (!recSec) return;
    const [hh, mm] = time.split(":").map((x) => parseInt(x, 10));
    const d = new Date(recSec * 1000);
    d.setHours(hh, mm, 0, 0);
    update("restitution", { seconds: Math.floor(d.getTime() / 1000) });
    setForceConfirm(null);
  };

  const handleAutoPlace = async (assignedInterventions: OrdreIntervention[]) => {
    if (!orgData?.id || assignedInterventions.length === 0) return;

    // Pose dans MyO (ordre lié à ServiceBox, sauf si Carvis fait foi) : ça peut prendre une
    // minute. On ne bloque pas la fiche : le suivi est dans le tableau des tâches de fond,
    // comme l'envoi Alpha.
    const taskId = `autoplace:${ordre.id}`;
    const taskLabel = `Planning atelier — ${form.name || ordre.name || "ordre"}`;
    setBackgroundTask(taskId, { label: taskLabel, status: "running", detail: "Placement des interventions…" });
    try {
      const results = await atelierApi.autoPlaceForOrdre({
        org_id: orgData.id,
        ordre_id: ordre.id,
        interventions: assignedInterventions.map((i) => ({
          intervention_id: i.id,
          label: i.label,
          assigned_to: i.assigned_to!,
          estimated_hours: i.estimated_hours || 1,
        })),
        // Ne touche pas aux blocs déjà posés des autres interventions.
        scoped: true,
      });
      const placedCount = new Set(results.placed.map((r) => r.intervention_id)).size;
      if (results.unplaced.length > 0) {
        setBackgroundTask(taskId, {
          status: "error",
          detail: `${placedCount} placée(s), ${results.unplaced.length} à répartir : ${results.unplaced[0].reason || "pas de créneau"}`,
        });
        setAutoPlaceResults(results);
        setShowAutoPlace("results");
      } else {
        setBackgroundTask(taskId, { status: "done", detail: `${placedCount} intervention(s) placée(s)` });
      }
      // Refresh atelier data
      fetchAtelierData(orgData.id);
    } catch (e) {
      console.error("Auto-place error:", e);
      setBackgroundTask(taskId, { status: "error", detail: "Erreur lors du placement automatique" });
      setShowAutoPlace(null);
    }
  };

  return (
    <>
      <BaseModal
        title={<>Modifier RDV — {form.num} <span className="text-neutral-100 text-sm font-normal ml-2">{ordre.id}</span></>}
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-4xl"
      >
        <InternetRdvBanner ordre={ordre} className="mx-4 mt-3" />
        <AlphaUnknownBanner ordre={ordre} className="mx-4 mt-3" />
        <SbSyncBanner ordre={ordre} className="mx-4 mt-3" />
        {/* Quick nav + Tabs */}
        <div className="px-4 pt-3 overflow-hidden flex flex-col gap-2">
          <div className="flex flex-row items-center gap-3">
            <button
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
              onClick={() => { onClose(); navigateTo({ route: `/ordre/${ordre.id}` }); }}
            >
              <Icon1 name="open" size={14} />
              Ouvrir la fiche ordre
            </button>
            {ordre.dossier_id && (
              <button
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
                onClick={() => { onClose(); navigateTo({ route: `/dossier/${ordre.dossier_id}` }); }}
              >
                <Icon1 name="open" size={14} />
                Ouvrir le dossier
              </button>
            )}
            {/* Client en sourdine : visible d'entrée, pas seulement dans le chat */}
            <ClientMuteBadge discussionId={parentDossier?.discussion_id || ordre.discussion_id} />
            <BookingOriginBadge ordre={ordre} />
            {/* Véhicule de remplacement : visible d'entrée, plus seulement dans
                le menu Actions (même pastille que la fiche ordre). */}
            {mobilities.length > 0 ? (
              <div className="ml-auto flex flex-row flex-wrap items-center gap-1.5">
                {mobilities.map(({ reservation, vehicle }) =>
                  vehicle ? (
                    <RentalVehicleBadge
                      key={reservation.id}
                      vehicle={vehicle}
                      reservationId={reservation.id}
                      onBeforeNavigate={onClose}
                    />
                  ) : null,
                )}
              </div>
            ) : rentalVehicle ? (
              <div className="ml-auto">
                <RentalVehicleBadge
                  vehicle={rentalVehicle}
                  reservationId={currentOrdre.rental_reservation_id}
                  onBeforeNavigate={onClose}
                />
              </div>
            ) : currentOrdre.mobility_option ? (
              <div className="ml-auto">
                <MobilityRequestChip mobilityOption={currentOrdre.mobility_option} />
              </div>
            ) : null}
          </div>

          {/* Drapeaux speciaux : la modale les laissait invisibles alors qu'on peut
              les poser depuis son propre menu d'actions. */}
          {(currentOrdre.flags || []).length > 0 && (
            <div className="flex flex-row flex-wrap items-center gap-1.5">
              {ORDRE_FLAGS.filter((f) => (currentOrdre.flags || []).includes(f)).map((flag) => {
                const cfg = ORDRE_FLAG_CONFIG[flag];
                return (
                  <div
                    key={flag}
                    className="flex flex-row items-center gap-1.5 px-2.5 py-1 rounded-lg"
                    style={{ background: cfg.bg, border: `1px solid ${cfg.color}30` }}
                  >
                    <Icon1 name={cfg.icon} size={14} color={cfg.color} />
                    <span className="text-h4 font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
                  </div>
                );
              })}
              {currentOrdre.flag_reason && (
                <span className="text-h4 text-neutral-500">— {currentOrdre.flag_reason}</span>
              )}
            </div>
          )}

          <div className="flex flex-row items-center justify-between gap-3">
            <ChunkyTabs
              activeTab={activeTab}
              tabs={tabsWithBadge}
              setActiveTab={handleSetActiveTab}
            />
            {/* onOrdreUpdated : la modale lit un ordre recu en prop (le planning ne la
                branche pas au store), donc un drapeau pose depuis le menu ne
                s'affichait qu'apres reouverture sans cette superposition locale. */}
            <OrdreActionsMenu
              ordre={currentOrdre}
              orgData={orgData}
              context="modal"
              payments={payments}
              rentalVehicle={rentalVehicle}
              mobilities={mobilities}
              hasFacture={hasFacture}
              serviceboxProxyAvailable={serviceboxProxyAvailable}
              sbEstimationLoading={sbEstimationLoading}
              onEdit={() => {}}
              onDelete={() => setShowDeleteConfirm(true)}
              onRequestFlagReason={(flag, newFlags) => { setFlagReasonText(""); setFlagReasonModal({ flag, newFlags }); }}
              onOrdreUpdated={(updated) => setTransferUpdates((prev) => ({ ...(prev || {}), ...updated }))}
              onShowReminder={() => setShowReminderModal(true)}
              onAddDocument={() => setSubModal("document")}
              onAddFacture={() => setShowFactureUpload(true)}
              onServiceBoxTransfer={() => setShowServiceBoxModal(true)}
              onPushInterventions={() => setShowSbInterventions(true)}
              onShowSyncHistory={() => setShowSbHistory(true)}
              onImportEstimation={() => { handleImportServiceBoxEstimation(); }}
              onShowSecondary={() => setSubModal("secondary")}
              onShowRental={() => setShowRentalModal(true)}
              onRemoveRental={async () => {
                try {
                  if (ordre.rental_reservation_id) await vehiclesApi.deleteReservation(ordre.rental_reservation_id);
                  const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                  useOrdreStore.getState().updateOrdre(updatedOrdre);
                  setRentalVehicle(null);
                  showNotification({ type: "success", message: "Véhicule de remplacement retiré" });
                } catch { showNotification({ type: "error", message: "Erreur" }); }
              }}
              onEditReservation={() => setShowEditRentalRes(true)}
              onEditMobility={(resId) => { setEditMobilityId(resId); setShowEditRentalRes(true); }}
              onRemoveMobility={async (resId) => {
                try {
                  await vehiclesApi.deleteReservation(resId);
                  const updatedOrdre = await ordresApi.get(ordre.id);
                  useOrdreStore.getState().updateOrdre(updatedOrdre);
                  setTransferUpdates((prev) => ({
                    ...(prev || {}),
                    rental_vehicle_id: updatedOrdre.rental_vehicle_id,
                    rental_reservation_id: updatedOrdre.rental_reservation_id,
                  }));
                  await reloadMobilities();
                  showNotification({ type: "success", message: "Mobilité retirée" });
                } catch { showNotification({ type: "error", message: "Erreur" }); }
              }}
              onShowContrat={() => setShowContratModal(true)}
              onShowTracking={() => setShowTrackingPreview(true)}
              onShowPayment={(type) => setShowPaymentModal(type)}
            />
          </div>
        </div>

        {/* Tab content — height adapts to the viewport so only THIS region scrolls
            (never the BaseModal body too) and the tabs + footer stay pinned. */}
        <div style={{ height: "min(480px, calc(90vh - 230px))" }} className="overflow-y-auto overflow-x-hidden">
          {activeTab === "ordre" && (
            <OrdreTab
              form={form}
              update={update}
              peopleOnOrdre={peopleOnOrdre}
              linkedVehicle={form.vehicle || parentDossier?.vehicles?.find((v) => v.id === form.vehicle_id) || null}
              dossierType={parentDossier?.type as DossierType | undefined}
              onDossierTypeChange={async (type) => {
                if (!parentDossier) return;
                const updated = await dossiersApi.update(parentDossier.id, { type: type || "particulier" } as any);
                setParentDossier(updated);
              }}
              recalls={recalls}
              receptionConflict={receptionConflict}
              onSwitchReception={switchReception}
              receptionDisabledSlots={recDisabledSlots}
              restitutionDisabledSlots={restDisabledSlots}
            />
          )}
          {activeTab === "interventions" && (
            <InterventionsTab
              interventions={form.interventions}
              interventionGroups={form.intervention_groups}
              atelierCharges={ordreAtelierCharges}
              onAdd={(g) => { setAddGroup(g); setSubModal("intervention"); }}
              onRemove={(idx) => update("interventions", form.interventions.filter((_, i) => i !== idx))}
              onToggle={(idx) => update("interventions", form.interventions.map((it, i) =>
                i === idx ? { ...it, status: (isAPrevoir(it) ? "inclus" : "a_prevoir") as OrdreIntervention["status"] } : it,
              ))}
              onEdit={(idx) => setEditingIntervention(idx)}
            />
          )}
          {activeTab === "contacts" && (
            <ContactsTab
              contacts={form.contacts || []}
              onAdd={() => { setEditingContact(null); setSubModal("contact"); }}
              onEdit={(c) => { setEditingContact(c); setSubModal("contact"); }}
              onSetPrimary={setPrimaryContact}
              onDelete={deleteContact}
              busyId={contactBusyId}
            />
          )}
          {activeTab === "vehicules" && (
            <VehiculesTab
              dossierVehicles={parentDossier?.vehicles || (form.vehicle ? [form.vehicle] : [])}
              selectedVehicleId={form.vehicle_id || form.vehicle?.id}
              onSelect={(vehicleId) => {
                const v = (parentDossier?.vehicles || []).find((veh) => veh.id === vehicleId);
                update("vehicle_id", vehicleId);
                if (v) {
                  update("vehicle", v);
                  if (v.plate) update("immatriculation", v.plate);
                  const vehicleModele = [v.marque, v.modele].filter(Boolean).join(" ");
                  if (vehicleModele) update("modele", vehicleModele);
                }
              }}
              onEdit={(v) => setEditingVehicle(v)}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              documents={mergedDocuments}
              onAdd={() => setSubModal("document")}
              onPreview={openDocPreview}
              onDelete={(doc) => setDocToDelete(doc)}
              owner={{ ordreId: ordre.id, dossierId: parentDossier?.id }}
              discussionId={parentDossier?.discussion_id || ordre.discussion_id}
            />
          )}
          {activeTab === "communications" && (
            <div style={{ height: "100%" }}>
              <ChatPanel
                discussionId={parentDossier?.discussion_id || ordre.discussion_id}
                internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
                entityType="ordre"
                entityId={ordre.id}
                entityName={ordre.name}
                compact
                whatsappLinked={!!(orgData?.whatsapp?.connected && (parentDossier?.phone || ordre.contacts?.[0]?.phone))}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-1 p-4 flex flex-row justify-end">
          <div className="flex flex-row gap-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="copy" size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(ordre.id);
                showNotification({ type: "default", message: "ID ordre copié" });
              }}
            >
              Copier ID
            </SecondaryButton>
            <SecondaryButton variant="small" onClick={onClose}>
              Fermer
            </SecondaryButton>
            <Button variant="small" label={saving ? "Enregistrement..." : "Enregistrer"} onClick={handleSaveClick} disabled={saving || !form.restitution?.seconds} />
          </div>
        </div>

      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer l'ordre ${form.num} ?`}
          message="Cette action est irréversible. L'ordre et ses rendez-vous associés seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          extraContent={ordre.servicebox_rdv_id ? (
            <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="flex flex-col flex-1">
                <span className="text-h4 font-medium text-neutral-800">Supprimer aussi dans ServiceBox</span>
                <span className="text-h5 text-neutral-400">Annule également le RDV dans le planning ServiceBox</span>
              </div>
              <Switch checked={deleteInServiceBox} onChange={setDeleteInServiceBox} />
            </div>
          ) : undefined}
          onConfirm={() => {
            // The decision is made; the two calls behind it (ServiceBox RDV via the garage's
            // proxy, then the ordre) are not the user's to watch. They run in the background
            // and report in the top-right task board.
            const sb = orgData?.servicebox;
            setDeleting(true);
            deleteOrdreInBackground({
              ordre,
              deleteInServiceBox,
              sbCreds: sb ? { username: sb.username || "", password: sb.password || "", site_code: sb.site_code, operator_email: operatorEmail } : undefined,
            });
            onDelete?.(ordre.id);
            onClose();
          }}
          confirmText="CONFIRMER"
        />
      )}

      {showFactureUpload && (
        <FactureUploadModal ordreId={ordre.id} onClose={() => setShowFactureUpload(false)} onSuccess={(updated) => { useOrdreStore.getState().updateOrdre(updated); setShowFactureUpload(false); }} />
      )}
      {showTrackingPreview && (
        <TrackingPreviewModal ordre={ordre} parentDossier={parentDossier} orgData={orgData} onClose={() => setShowTrackingPreview(false)} />
      )}
      {showRentalModal && orgData?.id && (
        <RentalVehicleModal ordre={ordre} orgId={orgData.id} existingReservations={mobilities.map((m) => m.reservation)} onClose={() => setShowRentalModal(false)} onAssign={(v, reservationId) => {
          reloadMobilities();
          // Attribuer = enregistrer. Le contrat de prêt reste dans le menu Actions ;
          // l'ouvrir d'office cachait la pastille juste obtenue. Les ids sont
          // superposés localement car la modale lit un ordre non branché au store.
          setRentalVehicle(v);
          setTransferUpdates((prev) => ({ ...(prev || {}), rental_vehicle_id: v.id, rental_reservation_id: reservationId }));
        }} />
      )}
      {showEditRentalRes && (
        <EditRentalReservationModal
          ordre={ordre}
          reservationId={editMobilityId || undefined}
          onClose={() => { setShowEditRentalRes(false); setEditMobilityId(null); }}
          onChanged={reloadMobilities}
        />
      )}
      {showContratModal && rentalVehicle && orgData && (
        <ContratLocationModal
          ordre={ordre}
          rentalVehicle={rentalVehicle}
          contact={ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0] || parentDossier?.contacts?.find((c) => c.is_primary) || parentDossier?.contacts?.[0]}
          clientVehicle={primaryVehicle}
          orgData={orgData}
          onClose={() => setShowContratModal(false)}
        />
      )}
      {showServiceBoxEstimation && (
        <EstimateExtractionModal ordre={ordre} initialExtraction={sbExtraction || undefined} isExtracting={sbEstimationLoading} onClose={() => { setShowServiceBoxEstimation(false); setSbExtraction(null); refreshFromServer(); }} />
      )}
      {sbPrChoices && sbPrChoices.length > 0 && currentOrdre.servicebox_dossier_id && (
        <ServiceBoxPrChoiceModal
          key={sbPrChoices.map((c) => c.value).join("|")}
          choices={sbPrChoices}
          creds={sbCreds()}
          dossierId={currentOrdre.servicebox_dossier_id}
          onResult={handlePrChoiceResult}
          onClose={() => setSbPrChoices(null)}
        />
      )}
      {sbForfaitChoices && sbForfaitChoices.length > 0 && (
        <BaseModal onClose={() => setSbForfaitChoices(null)} title="Choisir le forfait" startIcon={<Icon1 name="sett-servicebox" size={20} />} width="max-w-lg">
          <div className="flex flex-col gap-3 p-4">
            <span className="text-h3 text-neutral-500">ServiceBox propose plusieurs forfaits. Choisissez la variante à appliquer :</span>
            {Object.entries(sbForfaitChoices.reduce<Record<string, ForfaitChoice[]>>((acc, c) => { (acc[c.field] ||= []).push(c); return acc; }, {})).map(([field, opts]) => (
              <div key={field} className="flex flex-col gap-1.5">
                {opts.map((c) => {
                  const checked = sbForfaitSel[field] === c.code;
                  return (
                    <label key={c.code} className="flex flex-row items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer" style={{ border: checked ? "2px solid #000" : "1.5px solid #E5E5E5", background: checked ? "#F5F5F5" : "#fff" }}>
                      <input type="radio" name={field} checked={checked} onChange={() => setSbForfaitSel((p) => ({ ...p, [field]: c.code }))} />
                      <div className="flex flex-col flex-1">
                        <span className="text-h3 font-medium text-neutral-700">{c.code} — {c.designation}</span>
                        <span className="text-h4 text-neutral-400">{c.prix_ttc} € TTC{c.prix_ht ? ` · ${c.prix_ht} € HT` : ""}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            ))}
            <div className="flex flex-row gap-2 pt-1">
              <Button label={sbForfaitSubmitting ? "Application…" : "Valider et générer le devis"} onClick={submitForfaitChoice} disabled={sbForfaitSubmitting} variant="small" />
              <SecondaryButton label="Annuler" onClick={() => setSbForfaitChoices(null)} variant="small" />
            </div>
          </div>
        </BaseModal>
      )}
      {showPaymentModal && (
        <PaymentRequestModal ordre={ordre} defaultType={showPaymentModal} onClose={() => setShowPaymentModal(null)} onSuccess={() => { fetchPayments(); ordresApi.get(ordre.id).then((u) => useOrdreStore.getState().updateOrdre(u)).catch(() => {}); }} />
      )}
      {showServiceBoxModal && orgData?.servicebox && (
        <ServiceBoxTransferModal
          ordre={currentOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setShowServiceBoxModal(false)}
          onTransferred={(updates) => {
            setTransferUpdates((prev) => ({ ...(prev || {}), ...updates }));
            setForm((prev) => ({ ...prev, ...updates }));
          }}
        />
      )}
      {showSbInterventions && orgData?.servicebox && (
        <ServiceBoxUpdateModal
          ordre={currentOrdre}
          onClose={() => setShowSbInterventions(false)}
          onOrdreUpdated={(updated) => setTransferUpdates((prev) => ({ ...(prev || {}), ...updated }))}
        />
      )}
      {showSbHistory && (
        <OrdreSyncHistoryModal ordre={currentOrdre} onClose={() => setShowSbHistory(false)} />
      )}
      {sbAutoUpdateOrdre && orgData?.servicebox && (
        <ServiceBoxTransferModal
          autoRun
          ordre={sbAutoUpdateOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setSbAutoUpdateOrdre(null)}
        />
      )}
      {showReminderModal && ordre?.intervention_groups && (() => {
        const ig = ordre.intervention_groups;
        const activeToken = Object.values(ig).map((g) => g.devis_token).filter(Boolean).pop();
        return activeToken ? <DevisReminderModal onClose={() => setShowReminderModal(false)} tokenId={activeToken} /> : null;
      })()}
      {flagReasonModal && (
        <BaseModal
          title={`Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} »`}
          startIcon={<Icon1 name={ORDRE_FLAG_CONFIG[flagReasonModal.flag].icon} size={20} color={ORDRE_FLAG_CONFIG[flagReasonModal.flag].color} />}
          onClose={() => setFlagReasonModal(null)}
        >
          <div className="flex flex-col gap-3 p-4">
            <div className="text-h3 text-neutral-500">Raison (optionnel)</div>
            <input
              type="text"
              value={flagReasonText}
              onChange={(e) => setFlagReasonText(e.target.value)}
              placeholder="Ex : en attente de pièces..."
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              autoFocus
            />
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => setFlagReasonModal(null)}>Annuler</SecondaryButton>
            <Button
              variant="small"
              label="Confirmer"
              onClick={async () => {
                try {
                  const updated = await ordresApi.update(ordre.id, {
                    flags: flagReasonModal.newFlags,
                    ...(flagReasonText ? { flag_reason: flagReasonText } : {}),
                  } as any);
                  useOrdreStore.getState().updateOrdre(updated);
                  setTransferUpdates((prev) => ({ ...(prev || {}), ...updated }));
                  showNotification({ type: "success", message: `Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} » ajouté` });
                } catch {
                  showNotification({ type: "error", message: "Erreur" });
                }
                setFlagReasonModal(null);
              }}
            />
          </div>
        </BaseModal>
      )}

      {/* Sub-modals */}
      {subModal === "intervention" && (
        <InterventionPickerModal
          configuredInterventions={configuredInterventions}
          onCatalogUpdated={setConfiguredInterventions}
          group={addGroup}
          onAdd={(inters, autoPlace) => {
            update("interventions", [...form.interventions, ...inters]);
            if (autoPlace) setPendingAutoPlace((prev) => [...prev, ...inters.filter((i) => i.assigned_to).map((i) => i.id)]);
          }}
          onClose={() => setSubModal(null)}
        />
      )}
      {editingIntervention !== null && form.interventions[editingIntervention] && (
        <InterventionEditModal
          intervention={form.interventions[editingIntervention]}
          onClose={() => setEditingIntervention(null)}
          onSave={(patch) => update("interventions", form.interventions.map((it, i) =>
            i === editingIntervention ? { ...it, ...patch } : it,
          ))}
        />
      )}

      {shrinkPlan && !showAutoPlace && (
        <AdjustAtelierJobsModal plan={shrinkPlan} onClose={() => setShrinkPlan(null)} />
      )}

      {subModal === "contact" && (
        <ContactFormModal
          contact={editingContact}
          onClose={() => { setSubModal(null); setEditingContact(null); }}
          onSave={(data) => handleContactSave(editingContact, data)}
          saving={contactSaving}
        />
      )}
      {pendingContactData && (
        <ConfirmationModal
          title="Mettre à jour les coordonnées ?"
          message="Les coordonnées de ce contact principal sont utilisées pour les notifications, e-mails et messages WhatsApp. Confirmez-vous la modification ?"
          confirmLabel="Oui, mettre à jour"
          onClose={() => setPendingContactData(null)}
          onConfirm={() => { saveContactToDossier(pendingContactData.contact, pendingContactData.data); }}
        />
      )}
      {subModal === "vehicle" && <AddVehicleModal onClose={() => setSubModal(null)} />}
      {editingVehicle && (
        <VehicleEditModal
          vehicle={editingVehicle as ClientVehicle}
          onSave={(data) => saveVehicleToDossier(editingVehicle as ClientVehicle, data)}
          onClose={() => setEditingVehicle(null)}
          saving={vehicleSaving}
          timeline={parentDossier?.timeline || []}
          ordres={parentDossier?.ordres || []}
          documents={parentDossier?.documents}
          dossierId={parentDossier?.id}
        />
      )}
      {subModal === "document" && ordre.dossier_id && (
        <AddDocumentModal
          dossierId={ordre.dossier_id}
          onClose={() => setSubModal(null)}
          onSuccess={(updatedDossier) => setParentDossier(updatedDossier)}
        />
      )}
      {docToDelete && (
        <ConfirmationModal
          title="Supprimer ce document ?"
          message={`Le document « ${docToDelete.name} » sera définitivement supprimé. Cette action est irréversible.`}
          confirmLabel={deletingDoc ? "Suppression..." : "Supprimer"}
          onClose={() => { if (!deletingDoc) setDocToDelete(null); }}
          onConfirm={handleDeleteDocument}
        />
      )}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => {
            const a = document.createElement("a");
            a.href = previewDoc.url;
            a.download = previewDoc.doc.name;
            a.target = "_blank";
            a.click();
          }}
          {...galleryNav(previewDoc.gallery, previewDoc.doc?.id, (it, g) => openDocPreview(it.doc, g))}
        />
      )}
      {showRestitutionConfirm && (restitutionChanged && form.restitution?.seconds ? (
        <RestitutionChangeConfirmModal
          kind="restitution"
          oldSeconds={savedOrdre.restitution?.seconds}
          newSeconds={form.restitution.seconds}
          onClose={() => { setShowRestitutionConfirm(false); setPendingForce(false); }}
          onConfirm={(comment, notify) => handleSave(comment, notify, pendingForce)}
          saving={saving}
        />
      ) : receptionChanged && form.reception_date?.seconds ? (
        <RestitutionChangeConfirmModal
          kind="reception"
          oldSeconds={savedOrdre.reception_date?.seconds}
          newSeconds={form.reception_date.seconds}
          onClose={() => { setShowRestitutionConfirm(false); setPendingForce(false); }}
          onConfirm={(comment, notify) => handleSave(comment, notify, pendingForce)}
          saving={saving}
        />
      ) : null)}
      {forceConfirm && (
        <ForceConfirmModal
          title="Créneau non conforme"
          message={forceConfirm.errors[0]?.message || "Ce créneau ne respecte pas les règles de prise de RDV."}
          confirmLabel="Forcer ce RDV"
          allowForce={isForceable(forceConfirm)}
          onCancel={() => setForceConfirm(null)}
          onConfirm={() => {
            setForceConfirm(null);
            // Route through proceedSave so forcing still asks whether to notify
            // the client of the reception/restitution change before saving.
            proceedSave(true);
          }}
        >
          {forceConfirm.suggested_restitution_time && form.restitution?.seconds && (
            <button
              className="self-start text-h4 font-medium px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50"
              style={{ fontFamily: "Poppins", color: "#4F46E5" }}
              onClick={() => applySuggestedRestitution(forceConfirm.suggested_restitution_time!)}
            >
              Appliquer la restitution suggérée ({forceConfirm.suggested_restitution_time})
            </button>
          )}
        </ForceConfirmModal>
      )}
      {subModal === "secondary" && (
        <SecondaryOrdreModal
          ordre={ordre}
          onClose={() => { setSubModal(null); onClose(); }}
        />
      )}

      {/* Auto-place results */}
      {showAutoPlace === "results" && autoPlaceResults && (
        <BaseModal
          title="Résultat du placement"
          startIcon={<Icon1 name="planning" size={20} />}
          onClose={() => setShowAutoPlace(null)}
        >
          <div className="p-4 flex flex-col gap-4">
            {autoPlaceResults.placed.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-green-700">
                  {autoPlaceResults.placed.length} intervention(s) placée(s)
                </div>
                {autoPlaceResults.placed.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-green-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">
                      {r.worker_name} — {r.date ? new Date(r.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : ""} de {formatHour(r.start_hour || 0)} à {formatHour((r.start_hour || 0) + (r.duration_hours || 0))}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {autoPlaceResults.unplaced.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-orange-600">
                  {autoPlaceResults.unplaced.length} intervention(s) sans créneau
                </div>
                {autoPlaceResults.unplaced.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-orange-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">{r.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end">
            <Button variant="small" label="Fermer" onClick={() => setShowAutoPlace(null)} />
          </div>
        </BaseModal>
      )}
    </>
  );
}
